import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../services/api";
import { textoErrorApi } from "../utils/textoErrorApi";
import { mostrarAvisoVitalCasa } from "../utils/mostrarAvisoVitalCasa";
import { normalizeProvinciaId, normalizePoblacionId } from "../utils/normalizeLocalizacionIds";
import { nombrePoblacionPorId } from "../utils/poblacionesFavoritas";
import { useTheme } from "../components/ThemeProvider";
import SeccionCabecera from "../components/propiedades/SeccionCabecera";
import SeccionGenerales from "../components/propiedades/SeccionGenerales";
import SeccionDistribucion from "../components/propiedades/SeccionDistribucion";
import SeccionEquipamiento from "../components/propiedades/SeccionEquipamiento";
import SeccionUbicacion from "../components/propiedades/SeccionUbicacion";
import SeccionEconomicos from "../components/propiedades/SeccionEconomicos";
import SeccionAlquiler, { patchAlquilerDefaultsVacios } from "../components/propiedades/SeccionAlquiler";
import SeccionLocalizacion from "../components/propiedades/SeccionLocalizacion";
import SeccionMultimedia from "../components/propiedades/SeccionMultimedia";
import SeccionPublicacionWeb from "../components/propiedades/SeccionPublicacionWeb";
import SelectorIdiomaFicha from "../components/propiedades/SelectorIdiomaFicha";
import SeccionPropietarios from "../components/propiedades/SeccionPropietarios";
import {
  contactosAsociadosFichaDesdeExtras,
  propietariosFichaDesdeExtras,
} from "../components/captacion/BloquePropietariosAgente";
import SeccionDatosInternos from "../components/propiedades/SeccionDatosInternos";
import SeccionRegistro from "../components/propiedades/SeccionRegistro";
import SeccionNotaSimple from "../components/propiedades/SeccionNotaSimple";
import SeccionDocumentacion from "../components/propiedades/SeccionDocumentacion";
import SeccionHistorial from "../components/propiedades/SeccionHistorial";
import ModalConfirmLeadsBajadaPrecio from "../components/ModalConfirmLeadsBajadaPrecio";
import ModalEnvioMasivoFrios from "../components/ModalEnvioMasivoFrios";
import ModalRepublicarPortales, {
  cargarItemsPublicacionesPrevias,
} from "../components/propiedades/ModalRepublicarPortales";
import { mensajeDefaultBajada } from "../utils/bajadaPrecioUtils";
import { usePropiedadFormEmbed } from "../context/PropiedadFormEmbedContext";
import { markListsForRefresh } from "../utils/markListsRefresh";
import { separarSuperficiesFicha } from "../utils/superficiesFichaSplit";
import { useCaptacionSubidaFondo } from "../hooks/useCaptacionSubidaFondo";
import BarraSubidaFondo from "../components/captaciones/BarraSubidaFondo";
import {
  resolveCaptacionMediaUrl,
} from "../services/captacionUpload";
import { extrasCaptacionToFormPatch } from "../utils/captacionExtrasForm";
import { esParcelaBasuraTipo, normalizarDireccionRusticaCaptacion } from "../utils/parseDireccionRustica";
import { esOperacionAlquiler } from "../utils/esOperacionAlquiler";
import usePermiso from "../hooks/usePermiso";
import { extrasDomicilioTitularDesdePersona } from "../utils/domicilioContacto";
import { placesLocalityHintFromForm } from "../utils/placesLocalityHint";
import { mapFotoCaptacionDesdeApi, mapFotoPropiedadDesdeApi } from "../utils/fotoMultimediaUtils";
import { PropiedadIdiomaProvider } from "../context/PropiedadIdiomaContext";
import { IDIOMA_FUENTE } from "../constants/propiedadIdiomas";
import { esModoEdicionFuente, buildFormVista, makeLabelFn, buildEsTextMapFromLabels, translateUiString } from "../utils/propiedadI18n";
import { aplicarI18nAuxiliares } from "../utils/propiedadAuxiliarI18n";

/** Evita doble POST de captación en React 18 Strict Mode (dev). Valor = timestamp ms. */
const SS_ALTA_CAP_PIPELINE = "vitalcasa_alta_captacion_pipeline";
const SS_ALTA_CAP_PIPELINE_MAX_MS = 120_000;
/** Si se pierde location.state (refresh), recuperamos la ficha de captación en Visita concertada. */
const SS_FICHA_CAP_ID = "vitalcasa_ficha_captacion_id";

/** Ruta interna permitida para volver tras salir del formulario (evita open redirect). */
function sanitiseReturnTo(v) {
  if (typeof v !== "string" || !v.startsWith("/") || v.startsWith("//")) return null;
  if (v.includes("://")) return null;
  if (v.length > 2000) return null;
  return v;
}

/** Construye JSON de vídeos + adjunta archivos MP4/MOV/WebM para el mismo PUT/POST que las fotos. */
function appendVideosToFormData(fd, videosArchivo, videosYoutube) {
  const videosPayload = [];
  const archivosVideo = [];
  let orden = 0;
  for (const v of videosArchivo) {
    if (v.file) {
      videosPayload.push({
        tipo: "archivo",
        titulo: v.titulo || "",
        orden: orden++,
        _uploadIndex: archivosVideo.length,
      });
      archivosVideo.push(v.file);
    } else if (v.url) {
      videosPayload.push({
        tipo: "archivo",
        url: v.url,
        titulo: v.titulo || "",
        orden: orden++,
      });
    }
  }
  for (const v of videosYoutube) {
    const u = (v.url || "").trim();
    if (u) {
      videosPayload.push({
        tipo: "youtube",
        url: u,
        titulo: v.titulo || "",
        orden: orden++,
      });
    }
  }
  fd.append("videos", JSON.stringify(videosPayload));
  archivosVideo.forEach((f) => fd.append("videos_archivo", f));
}

function appendDocumentosToFormData(fd, documentos, documentosEliminar) {
  const nuevos = documentos.filter((d) => d.file);
  nuevos.forEach((d) => fd.append("documentos", d.file));
  fd.append(
    "documentos_meta",
    JSON.stringify(
      nuevos.map((d) => ({
        nombre_documento: d.nombre_documento,
        tipo_codigo: d.tipo_codigo || "documento_generico",
        descripcion_tipo: (d.descripcion_tipo && String(d.descripcion_tipo).trim()) || null,
      }))
    )
  );
  const existentes = documentos.filter((d) => d.existente && d.id);
  fd.append(
    "documentos_actualizar",
    JSON.stringify(
      existentes.map((d) => ({
        id: d.id,
        nombre_documento: d.nombre_documento,
        tipo_codigo: d.tipo_codigo || "documento_generico",
        descripcion_tipo: (d.descripcion_tipo && String(d.descripcion_tipo).trim()) || null,
      }))
    )
  );
  if (documentosEliminar.length > 0) {
    fd.append("documentos_eliminar", JSON.stringify(documentosEliminar));
  }
}

/** Campos de solo lectura / legacy que no deben ir en multipart al guardar propiedad. */
const FORM_OMITIR_EN_MULTIPART = new Set([
  "id", "contacto_comunicacion_id", "updated_at", "created_at",
  "poblacion", "poblacion_nombre", "zona_nombre", "tipo_nombre",
  "agente_nombre", "procedencia_nombre", "procedencia_emoji",
  "fotos", "videos", "precio",
]);

const SECCIONES = [
  { id: "cabecera",    label: "Principales",  color: "#2563eb", textBlanco: true  },
  { id: "generales",   label: "Generales",    color: "#059669", textBlanco: false },
  { id: "distribucion",label: "Distribución", color: "#7c3aed", textBlanco: true  },
  { id: "equipamiento",label: "Equipamiento", color: "#db2777", textBlanco: false },
  { id: "ubicacion",   label: "Ubicación",    color: "#0891b2", textBlanco: false },
  { id: "economicos",  label: "Económicos",   color: "#d97706", textBlanco: false },
  { id: "alquiler",    label: "Alquiler",     color: "#0d9488", textBlanco: true, soloAlquiler: true },
  { id: "localizacion",label: "Localización", color: "#ea580c", textBlanco: false },
  { id: "multimedia",  label: "Multimedia",   color: "#0284c7", textBlanco: false },
  { id: "publicacion", label: "Web",          color: "#16a34a", textBlanco: false },
  { id: "propietarios", label: "Propietarios y contactos", color: "#475569", textBlanco: true },
  { id: "internos",    label: "Internos",     color: "#4f46e5", textBlanco: false },
  { id: "registro",    label: "Registro",     color: "#be123c", textBlanco: true  },
  { id: "notasimple",  label: "Nota Simple",  color: "#0f766e", textBlanco: true  },
  { id: "documentacion", label: "Documentación", color: "#a16207", textBlanco: true },
  { id: "historial", label: "Historial", color: "#6366f1", textBlanco: false },
];

export default function PropiedadForm() {
  const navigate = useNavigate();
  const embed = usePropiedadFormEmbed();
  const { id } = useParams();
  const location = useLocation();
  const esEdicion = !!id;
  const fromStateCaptacion = embed?.embeddedCaptacion ?? location.state?.fromCaptacion ?? null;
  const altaCaptacionDesdePipeline = !!location.state?.altaCaptacionDesdePipeline;
  const pendingNuevaCaptacion = !!(location.state?.pendingNuevaCaptacion || altaCaptacionDesdePipeline);
  /** Alta nueva en blanco: no reutilizar un borrador previo de sessionStorage. */
  const entradaDirectaNueva = !!(
    location.state?.entradaDirectaNueva
    || (pendingNuevaCaptacion && !fromStateCaptacion?.id && !embed?.embeddedCaptacion?.id)
  );
  const fromCapIdEarly = fromStateCaptacion?.id;
  const [captacionFromRecovery, setCaptacionFromRecovery] = useState(null);
  const captacionOrigen = fromStateCaptacion || captacionFromRecovery;
  const clearFichaCaptacionSession = () => {
    try {
      sessionStorage.removeItem(SS_FICHA_CAP_ID);
    } catch {
      /* ignore */
    }
  };
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();

  const [form, setForm] = useState({
    disponible: true,
    disponible_desde: new Date().toISOString().split("T")[0],
    motivo_no_disponible: "",
    fecha_alta: new Date().toISOString().split("T")[0],
    operacion: "", tipo_inmueble_id: "",
    etiqueta_id: "", exclusiva: false,
    exclusiva_duracion: "", exclusiva_desde: "", exclusiva_hasta: "",
    publicar_web: false, precio_web_consultar: false,
    titulo: "", referencia: "", referencia_manual: false, iva_porcentaje: 21,
  });
  const [auxiliares, setAuxiliares] = useState({});
  const [superficies, setSuperficies] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [videosArchivo, setVideosArchivo] = useState([]);
  const [videosYoutube, setVideosYoutube] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [documentosEliminar, setDocumentosEliminar] = useState([]);
  const [propietarios, setPropietarios] = useState([]);
  const [contactosAsociados, setContactosAsociados] = useState([]);
  const [contactoComunicacionId, setContactoComunicacionId] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [error, setError] = useState("");
  const [camposError, setCamposError] = useState([]);
  const [modalCamposFaltantes, setModalCamposFaltantes] = useState([]);
  const [mostrarModalRevisar, setMostrarModalRevisar] = useState(false);
  /** 'propiedad' = alta inventario incompleta; 'alta_directa' = Nueva captación → Captaciones */
  const [modalFaltantesModo, setModalFaltantesModo] = useState("propiedad");
  const [seccionActiva, setSeccionActiva] = useState("cabecera");
  const [historicoPreciosTick, setHistoricoPreciosTick] = useState(0);
  /** Tras bajada significativa de precio: confirmación + envío masivo opcional. */
  const [bajadaFlow, setBajadaFlow] = useState(null);
  const [modalEnvioBajada, setModalEnvioBajada] = useState(false);
  const [leadsBajadaEnvio, setLeadsBajadaEnvio] = useState([]);
  const [modalPortalesPrevios, setModalPortalesPrevios] = useState(null);
  const disponibleAlCargarRef = useRef(null);
  const [focusPrecioTick, setFocusPrecioTick] = useState(0);
  const [focusLlavesTick, setFocusLlavesTick] = useState(0);
  const focusLlavesPendingRef = useRef(!!location.state?.focusLlaves);
  /** Corrección desde panel Fallos de Portales: publicar al portal tras guardar. */
  const portalSyncFixRef = useRef(
    location.state?.portalSyncFix && typeof location.state.portalSyncFix === "object"
      ? location.state.portalSyncFix
      : null
  );
  const portalSyncFocusDoneRef = useRef(false);
  const [fichaCargada, setFichaCargada] = useState(false);
  const [hoverNav, setHoverNav] = useState(null);
  const [idiomaVista, setIdiomaVista] = useState(IDIOMA_FUENTE);
  const [labelsMap, setLabelsMap] = useState({});
  const [auxiliaresI18nMap, setAuxiliaresI18nMap] = useState({});
  const soloLecturaIdioma = !esModoEdicionFuente(idiomaVista);
  const tLabel = useMemo(
    () => makeLabelFn(labelsMap, idiomaVista),
    [labelsMap, idiomaVista],
  );
  const esTextMap = useMemo(
    () => buildEsTextMapFromLabels(labelsMap, idiomaVista),
    [labelsMap, idiomaVista],
  );
  const tvLabel = useCallback(
    (text) => translateUiString(idiomaVista, text, esTextMap),
    [idiomaVista, esTextMap],
  );
  const auxiliaresVista = useMemo(
    () => aplicarI18nAuxiliares(auxiliares, auxiliaresI18nMap, idiomaVista),
    [auxiliares, auxiliaresI18nMap, idiomaVista],
  );
  const formVista = useMemo(
    () => buildFormVista(form, idiomaVista),
    [form, idiomaVista],
  );
  const [borradorGuardadoAt, setBorradorGuardadoAt] = useState(null);
  const [autosaveActivo, setAutosaveActivo] = useState(false);
  const autosaveTimerRef = useRef(null);
  const autosaveSnapshotRef = useRef("");
  const guardandoBorradorRef = useRef(false);
  /** Tras «Cancelar»: no volver a persistir al desmontar / cerrar overlay. */
  const omitirFlushAlSalirRef = useRef(false);
  const maybeRecoverCaptacion =
    typeof sessionStorage !== "undefined" &&
    !esEdicion &&
    !fromCapIdEarly &&
    !pendingNuevaCaptacion &&
    !!sessionStorage.getItem(SS_FICHA_CAP_ID);
  const [cargando, setCargando] = useState(
    esEdicion || !!fromCapIdEarly || maybeRecoverCaptacion
  );
  const observerRef = useRef(null);
  const idsReportajeInicialRef = useRef(new Set());
  /**
   * Ref que persiste el objeto captación durante toda la vida del componente,
   * independientemente de los ciclos de location.state / React state.
   * Es el fallback de resolverCaptacionAlGuardar cuando captacionOrigen es null.
   */
  const captacionOrigenRef = useRef(null);
  /** Id de captación creada en ESTA apertura «nueva en blanco» (no el borrador anterior). */
  const captacionAltaBlancoIdRef = useRef(null);
  /** Refs frescos para evitar closures stale en PATCH de multimedia (no debe pisar superficies/propietarios). */
  const superficiesRef = useRef(superficies);
  const formRef = useRef(form);
  const propietariosRef = useRef(propietarios);
  const contactosAsociadosRef = useRef(contactosAsociados);
  const contactoComunicacionIdRef = useRef(contactoComunicacionId);
  useEffect(() => { superficiesRef.current = superficies; }, [superficies]);
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { propietariosRef.current = propietarios; }, [propietarios]);
  useEffect(() => { contactosAsociadosRef.current = contactosAsociados; }, [contactosAsociados]);
  useEffect(() => { contactoComunicacionIdRef.current = contactoComunicacionId; }, [contactoComunicacionId]);
  const [captacionVinculadaId, setCaptacionVinculadaId] = useState(null);
  const [captacionValidada, setCaptacionValidada] = useState(false);
  const returnToRef = useRef(sanitiseReturnTo(embed?.embeddedReturnTo ?? location.state?.returnTo));
  /** Estado a restaurar en destino (p. ej. Portales con Fallos abiertos). */
  const returnToStateRef = useRef(
    location.state?.returnToState && typeof location.state.returnToState === "object"
      ? location.state.returnToState
      : null
  );

  useEffect(() => {
    if (captacionOrigen?.id) captacionOrigenRef.current = captacionOrigen;
  }, [captacionOrigen?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = sanitiseReturnTo(embed?.embeddedReturnTo ?? location.state?.returnTo);
    if (s) returnToRef.current = s;
    if (location.state?.returnToState && typeof location.state.returnToState === "object") {
      returnToStateRef.current = location.state.returnToState;
    }
  }, [embed?.embeddedReturnTo, location.state?.returnTo, location.state?.returnToState]);

  /**
   * Definitivo: Cancelar / salir de la ficha NUNCA archiva el borrador.
   * Los borradores de «Nueva captación» solo se eliminan con el botón
   * «Eliminar» del modal Borradores (confirmación explícita + confirmar_borrador).
   * Antes, Cancelar tras autosave/Finalizar fallido/refresco borraba refs reales
   * (p. ej. 3664-C) porque borradorExplicitoRef se perdía al remount.
   */
  const cancelarFormulario = async () => {
    omitirFlushAlSalirRef.current = true;
    clearFichaCaptacionSession();
    navigateFormExit({ skipFlush: true });
  };

  /** Tras guardar/cancelar: vuelve a la pantalla de origen si se pasó `state.returnTo`; si no, pipeline (flujo captación) o listado propiedades. */
  const navigateFormExit = (opts = {}) => {
    markListsForRefresh();
    if (embed?.onClose) {
      embed.onClose(opts);
      return;
    }
    // Ojo: NO usar captacionOrigenRef.current?.id aquí. Ese ref también se
    // rellena al editar una propiedad YA existente que simplemente tiene un
    // captacion_vinculada_id histórico (ver cargarPropiedad), y eso hacía que
    // editar cualquier propiedad nacida de una captación te devolviera siempre
    // al Pipeline aunque hubieras entrado desde el listado de Propiedades.
    // Solo forzamos el pipeline de captaciones cuando el flujo actual ES
    // realmente "convertir captación en propiedad" (alta nueva o recuperación
    // de borrador), no al editar una ficha ya publicada.
    const hasCaptacion =
      pendingNuevaCaptacion ||
      fromStateCaptacion?.id ||
      captacionFromRecovery?.id;

    // Si hay contexto de captación (alta nueva), siempre ir al pipeline de
    // captaciones. Ignoramos returnTo genérico ('/pipeline') para no acabar
    // en el tab de leads.
    if (hasCaptacion) {
      navigate("/pipeline?modo=captaciones");
      return;
    }

    const rt = returnToRef.current;
    if (rt) {
      const restore = returnToStateRef.current;
      navigate(rt, restore ? { state: restore } : undefined);
      // Si restauramos Fallos abiertos, Portales hace scroll al panel; si no, arriba.
      if (!restore?.openFallos) {
        window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }), 0);
        window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }), 50);
      }
      return;
    }
    navigate("/propiedades");
  };

  const finalizarTrasBajadaPrecio = () => {
    setBajadaFlow(null);
    setModalEnvioBajada(false);
    setLeadsBajadaEnvio([]);
    navigateFormExit();
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [labelsRes, auxRes] = await Promise.all([
          api.get("/propiedades/i18n/labels"),
          api.get("/propiedades/i18n/auxiliares"),
        ]);
        if (!cancel) {
          if (labelsRes.data?.data) setLabelsMap(labelsRes.data.data);
          if (auxRes.data?.data) setAuxiliaresI18nMap(auxRes.data.data);
        }
      } catch {
        /* labels/aux i18n opcionales — fallback ES en UI */
      }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.find(e => e.isIntersecting);
        if (visible) setSeccionActiva(visible.target.id.replace("seccion-", ""));
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    // Observar nodos presentes en el DOM (la sección Alquiler solo existe si aplica)
    SECCIONES.forEach(s => {
      const el = document.getElementById("seccion-" + s.id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [cargando, form.operacion]);

  const precargarDesdeCaptacion = async (cap) => {
    // El listado del pipeline omite web_desc_* (payload slim). Pedir GET completo.
    let capFull = cap;
    if (cap?.id) {
      try {
        const res = await api.get(`/captaciones/${cap.id}`);
        if (res.data?.data?.id) capFull = res.data.data;
      } catch { /* usar cap de entrada */ }
    }
    const extras = (typeof capFull.datos_extras === 'object' && capFull.datos_extras !== null)
      ? capFull.datos_extras
      : (() => { try { return JSON.parse(capFull.datos_extras || '{}'); } catch { return {}; } })();

    // Resolver tipo_inmueble (puede ser nombre o UUID)
    let tipoInmuebleId = extras.tipo_inmueble_id || '';
    if (!tipoInmuebleId && capFull.tipo_inmueble) {
      try {
        const res = await api.get('/auxiliares/tipo-inmueble');
        const tipos = res.data.data || [];
        const match = tipos.find(t => t.nombre?.toLowerCase() === String(capFull.tipo_inmueble).toLowerCase());
        if (match) tipoInmuebleId = match.id;
      } catch {}
    }

    // Resolver poblacion (puede ser nombre o UUID)
    let poblacionId = extras.poblacion_id || '';
    if (!poblacionId && capFull.poblacion) {
      try {
        const prov = extras.provincia_id || extras.provincia || '';
        const res = await api.get(`/auxiliares/poblacion?provincia=${prov}`);
        const pobs = res.data.data || res.data || [];
        const match = pobs.find(p => p.nombre?.toLowerCase() === String(capFull.poblacion).toLowerCase());
        if (match) poblacionId = match.id;
      } catch {}
    }

    // Resolver procedencia (captación fuente → aux_fuentes)
    let procedenciaId = extras.procedencia_id || extras.fuente_id || '';
    if (!procedenciaId && capFull.fuente) {
      try {
        const res = await api.get('/auxiliares/procedencias');
        const fuentes = res.data.data || [];
        const match = fuentes.find(f => f.nombre?.toLowerCase() === String(capFull.fuente).toLowerCase());
        if (match) procedenciaId = match.id;
      } catch {}
    }

    const fromExtras = extrasCaptacionToFormPatch(extras);

    // Ampliar: polígono/parcelas que el móvil dejó en `numero` → parcela/poligono;
    // no reutilizar "Agraria" como nº de parcela; hidratar población legible.
    const rusticaPatch = normalizarDireccionRusticaCaptacion({
      nombre_via: capFull.calle || extras.nombre_via || extras.calle || '',
      calle: capFull.calle || extras.calle || extras.nombre_via || '',
      numero: capFull.numero || extras.numero || '',
      direccion: extras.direccion || '',
      parcela: extras.parcela,
      poligono: extras.poligono,
    }) || {};
    const parcelaLimpia = esParcelaBasuraTipo(extras.parcela)
      ? (rusticaPatch.parcela || '')
      : (rusticaPatch.parcela || extras.parcela || '');
    const pobNombre = String(
      extras.poblacion_nombre
      || (extras.poblacion && !/^\d{4,5}$/.test(String(extras.poblacion).trim()) ? extras.poblacion : '')
      || '',
    ).trim();

    const mapped = {
      operacion: capFull.operacion || extras.operacion || '',
      tipo_inmueble_id: tipoInmuebleId,
      nombre_via: rusticaPatch.nombre_via || capFull.calle || extras.nombre_via || '',
      numero: rusticaPatch.numero != null
        ? rusticaPatch.numero
        : (capFull.numero || extras.numero || ''),
      parcela: parcelaLimpia,
      poligono: rusticaPatch.poligono || extras.poligono || '',
      bloque: extras.bloque || '',
      escalera: extras.escalera || '',
      piso: extras.piso || extras.planta || '',
      puerta: extras.puerta || '',
      codigo_postal: extras.codigo_postal || '',
      provincia_id: extras.provincia_id || extras.provincia || '',
      poblacion_id: poblacionId,
      poblacion_nombre: pobNombre,
      latitud: extras.latitud ?? '',
      longitud: extras.longitud ?? '',
      tipo_via_id: extras.tipo_via_id || '',
      agente_id: capFull.agente_id || extras.agente_id || '',
      procedencia_id: procedenciaId,
      titulo: extras.titulo || extras.web_titulo || '',
      web_titulo: extras.web_titulo || extras.titulo || '',
      web_desc_corta_es: extras.web_desc_corta_es || '',
      web_desc_larga_es: extras.web_desc_larga_es || extras.descripcion_es || extras.descripcion_larga || extras.descripcion || '',
      precio_venta: extras.precio_venta || capFull.precio_venta || '',
      precio_propietario: extras.precio_propietario || '',
      honorarios_porcentaje: extras.honorarios_porcentaje || '',
      honorarios_euros: extras.honorarios_euros || '',
      iva_euros: extras.iva_euros || '',
      total_honorarios: extras.total_honorarios || '',
      iva_porcentaje: extras.iva_porcentaje != null && extras.iva_porcentaje !== '' ? extras.iva_porcentaje : undefined,
      gastos_comunidad: extras.gastos_comunidad || '',
      valoracion: extras.valoracion || '',
      // boolOrUndef: solo devuelve true/false si el campo existe en extras; si no, undefined (se filtra)
      publicar_web: extras.publicar_web != null && extras.publicar_web !== '' ? (extras.publicar_web === true || extras.publicar_web === 'true') : undefined,
      disponible: extras.disponible != null && extras.disponible !== '' ? (extras.disponible === true || extras.disponible === 'true') : undefined,
      disponible_desde: extras.disponible_desde || new Date().toISOString().split('T')[0],
      exclusiva: extras.exclusiva != null && extras.exclusiva !== '' ? (extras.exclusiva === true || extras.exclusiva === 'true') : undefined,
      exclusiva_duracion: (() => {
        const d = extras.exclusiva_duracion;
        if (d == null || d === '' || d === true || d === false || d === 'true' || d === 'false') return undefined;
        return String(d);
      })(),
      exclusiva_desde: extras.exclusiva_desde || '',
      exclusiva_hasta: extras.exclusiva_hasta || '',
      num_dormitorios: extras.num_dormitorios || '',
      num_banos: extras.num_banos || '',
      num_aseos: extras.num_aseos || '',
      m2_construidos: extras.m2_construidos || '',
      m2_parcela: extras.m2_parcela || '',
      m2_utiles: extras.m2_utiles || '',
      m2_terrazas: extras.m2_terrazas || '',
      plantas_edificio: extras.plantas_edificio || '',
      altura_planta: extras.altura_planta != null && extras.altura_planta !== ''
        ? extras.altura_planta
        : (function () {
            const raw = extras.piso || extras.planta || '';
            const s = String(raw).trim().toLowerCase();
            if (!s) return '';
            if (/^(bajo|pb|planta\s*baja|0)$/i.test(s)) return 0;
            const m = String(raw).match(/(\d{1,2})/);
            if (!m) return '';
            const n = parseInt(m[1], 10);
            return Number.isFinite(n) ? n : '';
          })(),
      ano_construccion: extras.ano_construccion || '',
      suelos_id: extras.suelos_id || '',
      carpinteria_int_id: extras.carpinteria_int_id || extras.carpinteria_interior_id || '',
      carpinteria_ext_id: extras.carpinteria_ext_id || extras.carpinteria_exterior_id || '',
      orientacion_id: extras.orientacion_id || '',
      // Ampliar: nº plaza si viene en extras o en detalle por plaza ('' se filtra en cleaned)
      num_garaje: (() => {
        const plano = extras.num_garaje;
        if (plano != null && String(plano).trim() !== '') return String(plano).trim();
        const det = extras.garajes_plazas_detalle;
        if (!Array.isArray(det)) return undefined;
        const nums = det.map((pl) => (pl && pl.numero != null ? String(pl.numero).trim() : '')).filter(Boolean);
        if (!nums.length) return undefined;
        return nums.length === 1 ? nums[0] : nums.join(', ');
      })(),
      num_trastero: extras.num_trastero != null && String(extras.num_trastero).trim() !== ''
        ? String(extras.num_trastero).trim()
        : undefined,
      amueblado: extras.amueblado != null && extras.amueblado !== '' ? (extras.amueblado === true || extras.amueblado === 'true') : undefined,
      inventario_muebles: extras.inventario_muebles && typeof extras.inventario_muebles === 'object'
        ? extras.inventario_muebles
        : undefined,
      vpo: extras.vpo != null && extras.vpo !== '' ? (extras.vpo === true || extras.vpo === 'true') : undefined,
      vpo_ano_fin: (() => {
        if (extras.vpo_ano_fin != null && String(extras.vpo_ano_fin).trim() !== '') return extras.vpo_ano_fin;
        const vf = extras.vpo_fecha_fin;
        if (vf && /^\d{4}-\d{2}-\d{2}/.test(String(vf))) return String(vf).slice(0, 4);
        if (vf && /^\d{4}$/.test(String(vf).trim())) return String(vf).trim();
        return '';
      })(),
      eq_ascensor: extras.eq_ascensor != null && extras.eq_ascensor !== '' ? (extras.eq_ascensor === true || extras.eq_ascensor === 'true') : undefined,
      // Ampliar: APK select_native guarda tipo en *_id; si hay tipo → Sí (vínculo bool↔tipo).
      eq_piscina_priv: (() => {
        const id = extras.eq_piscina_priv_id;
        if (id != null && String(id).trim() !== '') return true;
        if (extras.eq_piscina_priv != null && extras.eq_piscina_priv !== '') {
          if (extras.eq_piscina_priv === true || extras.eq_piscina_priv === 'true') return true;
          if (extras.eq_piscina_priv === false || extras.eq_piscina_priv === 'false') return false;
          // UUID en clave padre
          if (/^[0-9a-f-]{36}$/i.test(String(extras.eq_piscina_priv))) return true;
        }
        return undefined;
      })(),
      eq_piscina_priv_id: (() => {
        if (extras.eq_piscina_priv_id) return extras.eq_piscina_priv_id;
        const p = extras.eq_piscina_priv;
        if (p && typeof p === 'string' && /^[0-9a-f-]{36}$/i.test(p)) return p;
        return undefined;
      })(),
      eq_piscina_comun: (() => {
        const id = extras.eq_piscina_comun_id;
        if (id != null && String(id).trim() !== '') return true;
        if (extras.eq_piscina_comun != null && extras.eq_piscina_comun !== '') {
          if (extras.eq_piscina_comun === true || extras.eq_piscina_comun === 'true') return true;
          if (extras.eq_piscina_comun === false || extras.eq_piscina_comun === 'false') return false;
          if (/^[0-9a-f-]{36}$/i.test(String(extras.eq_piscina_comun))) return true;
        }
        return undefined;
      })(),
      eq_piscina_comun_id: (() => {
        if (extras.eq_piscina_comun_id) return extras.eq_piscina_comun_id;
        const p = extras.eq_piscina_comun;
        if (p && typeof p === 'string' && /^[0-9a-f-]{36}$/i.test(p)) return p;
        return undefined;
      })(),
      calificacion: extras.calificacion || '',
      firma_captacion: extras.firma_captacion || '',
    };

    // Solo poner valores no vacíos; fromExtras trae referencia, equipamiento, observaciones, web, etc.
    const cleaned = {};
    Object.entries({ ...fromExtras, ...mapped }).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) cleaned[k] = v;
    });

    setForm(prev => ({ ...prev, ...cleaned }));

    if (Array.isArray(extras.superficies_ficha)) {
      setSuperficies(separarSuperficiesFicha(extras.superficies_ficha));
    }

    const fichaProps = propietariosFichaDesdeExtras(extras);
    if (fichaProps.length > 0) {
      setPropietarios(
        fichaProps.map((p) => ({
          ...p,
          id: p.id || globalThis.crypto?.randomUUID?.() || `p-${Math.random().toString(36).slice(2)}`,
        }))
      );
    } else if (String(extras.propietario || "").trim()) {
      const t = String(extras.propietario).trim();
      setPropietarios([
        {
          id: globalThis.crypto?.randomUUID?.() || `p-${Math.random().toString(36).slice(2)}`,
          nombre: t,
          apellidos: "",
          telefono: "",
          email: "",
          porcentaje: 100,
        },
      ]);
    } else {
      setPropietarios([]);
    }

    if (Array.isArray(extras.contactos_asociados_ficha)) {
      setContactosAsociados(
        extras.contactos_asociados_ficha.map((c) => ({
          ...c,
          id: c.id || globalThis.crypto?.randomUUID?.() || `c-${Math.random().toString(36).slice(2)}`,
        })),
      );
    } else {
      const fichaCont = contactosAsociadosFichaDesdeExtras(extras);
      if (fichaCont.length > 0) {
        setContactosAsociados(
          fichaCont.map((c) => ({
            ...c,
            id: c.id || globalThis.crypto?.randomUUID?.() || `c-${Math.random().toString(36).slice(2)}`,
          })),
        );
      } else {
        setContactosAsociados([]);
      }
    }

    if (extras.contacto_comunicacion_id) {
      setContactoComunicacionId(extras.contacto_comunicacion_id);
    } else {
      const propsF = Array.isArray(extras.propietarios_ficha) ? extras.propietarios_ficha : [];
      const contsF = Array.isArray(extras.contactos_asociados_ficha) ? extras.contactos_asociados_ficha : [];
      const flagged = [...propsF, ...contsF].find((p) => p && (p.comunicacion || p.contacto_principal));
      if (flagged?.contacto_id) {
        setContactoComunicacionId(flagged.contacto_id);
      } else if (flagged?.id) {
        setContactoComunicacionId(flagged.id);
      } else if (extras.comunicacion_key) {
        setContactoComunicacionId(extras.comunicacion_key);
      } else {
        setContactoComunicacionId(null);
      }
    }

    try {
      const fr = await api.get(`/captaciones/${cap.id}/fotos-reportaje`);
      const list = fr.data.fotos || [];
      idsReportajeInicialRef.current = new Set(list.map((x) => x.id).filter(Boolean));
      setFotos(list.map((f) => mapFotoCaptacionDesdeApi(f)));
    } catch {
      idsReportajeInicialRef.current = new Set();
    }

    // Cargar documentos de la captación
    try {
      const dr = await api.get(`/captaciones/${cap.id}/documentos`);
      setDocumentos(
        (dr.data.data || []).map((d) => ({
          id: d.id,
          existente: true,
          url: d.url,
          preview: d.url,
          nombre_documento: d.nombre_documento || d.nombre_original || "Documento",
          tipo_codigo: d.tipo_codigo || "documento_generico",
          descripcion_tipo: d.descripcion_tipo || "",
          mime: d.mime || "",
          fecha: d.created_at,
        }))
      );
    } catch {
      setDocumentos([]);
    }

    try {
      const vr = await api.get(`/captaciones/${cap.id}/videos-reportaje`);
      const vlist = vr.data?.videos || [];
      setVideosArchivo(
        vlist.map((v) => ({
          id: v.id,
          url: v.url,
          preview: resolveCaptacionMediaUrl(v.url),
          titulo: v.titulo || "",
          existente: true,
          subidaEstado: "ok",
        }))
      );
    } catch {
      setVideosArchivo([]);
    }
    setDocumentosEliminar([]);
  };

  const cargarAuxiliares = async () => {
    const tablas = [
      "tipo-inmueble","etiqueta","estado-inmueble","orientacion","calefaccion",
      "aire-acondicionado","piscina","muebles","vistas","cocina",
      "carpinteria-interior","carpinteria-exterior","suelos",
      "tipo-via","zona","urbanizacion","procedencias"
    ];
    const res = await Promise.all(
      tablas.map(t => api.get("/auxiliares/" + t).then(r => ({ [t]: r.data.data })).catch(() => ({ [t]: [] })))
    );
    const aux = {};
    res.forEach(r => Object.assign(aux, r));
    setAuxiliares(aux);
  };

  const cargarPropiedad = async () => {
    try {
      const res = await api.get("/propiedades/" + id);
      const data = res.data.data;
      const formData = {};
      Object.keys(data).forEach(key => {
        if (key === "contacto_comunicacion_id") return;
        if (FORM_OMITIR_EN_MULTIPART.has(key) || key.startsWith("hist_")) return;
        if (data[key] !== null && data[key] !== undefined) {
          if (key.includes("desde") || key.includes("hasta") || key === "fecha_alta") {
            if (data[key]) formData[key] = data[key].substring(0, 10);
          } else if (key === "num_garaje" || key === "num_trastero") {
            formData[key] = data[key] != null && data[key] !== "" ? String(data[key]) : "";
          } else {
            formData[key] = data[key];
          }
        }
      });
      if (!formData.iva_porcentaje) formData.iva_porcentaje = 21;
      if (formData.provincia_id != null && formData.provincia_id !== "") {
        formData.provincia_id = normalizeProvinciaId(formData.provincia_id);
      }
      if (formData.poblacion_id != null && formData.poblacion_id !== "") {
        formData.poblacion_id = normalizePoblacionId(formData.poblacion_id);
      }
      if (formData.poblacion_id && formData.poblacion_nombre) {
        formData.poblacion = "";
      }
      setForm(prev => ({ ...prev, ...formData }));
      disponibleAlCargarRef.current =
        formData.disponible === true ||
        formData.disponible === "true" ||
        formData.disponible === 1 ||
        formData.disponible === "1";
      if (data.captacion_vinculada_id) {
        setCaptacionVinculadaId(data.captacion_vinculada_id);
        setCaptacionValidada(!!data.captacion_fecha_validacion);
        captacionOrigenRef.current = {
          ...(captacionOrigenRef.current || {}),
          id: data.captacion_vinculada_id,
        };
        // Textos web + inventario muebles desde captación vinculada (PWA).
        try {
          const capRes = await api.get(`/captaciones/${data.captacion_vinculada_id}`);
          const exRaw = capRes.data?.data?.datos_extras;
          const extras = (exRaw && typeof exRaw === "object") ? exRaw : {};
          const patch = {};
          if (!String(formData.web_desc_larga_es || "").trim() && extras.web_desc_larga_es) {
            patch.web_desc_larga_es = extras.web_desc_larga_es;
          }
          if (!String(formData.web_desc_corta_es || "").trim() && extras.web_desc_corta_es) {
            patch.web_desc_corta_es = extras.web_desc_corta_es;
          }
          const tit = String(extras.web_titulo || extras.titulo || "").trim();
          if (!String(formData.web_titulo || "").trim() && tit) {
            patch.web_titulo = tit;
          }
          if (extras.inventario_muebles && typeof extras.inventario_muebles === "object") {
            patch.inventario_muebles = extras.inventario_muebles;
          }
          if (Object.keys(patch).length) {
            setForm((prev) => ({ ...prev, ...patch }));
          }
          captacionOrigenRef.current = {
            ...(captacionOrigenRef.current || {}),
            id: data.captacion_vinculada_id,
            datos_extras: extras,
          };
        } catch { /* ignore */ }
      }
      try { const r = await api.get("/propiedades/" + id + "/superficies"); setSuperficies(separarSuperficiesFicha(r.data.data || [])); } catch {}
      try {
        const r = await api.get("/propiedades/" + id + "/fotos");
        setFotos((r.data.data || []).map((f) => mapFotoPropiedadDesdeApi(f)));
      } catch {}
      try {
        const r = await api.get("/propiedades/" + id + "/videos");
        const raw = r.data.data || [];
        const apiOrigin = (import.meta.env.VITE_API_URL || "http://localhost:4000").replace(/\/$/, "");
        const yt = [];
        const arch = [];
        for (const v of raw) {
          const tipo = String(v.tipo || "youtube").toLowerCase();
          const vid = v.id || crypto.randomUUID();
          const titulo = v.titulo || "";
          if (tipo === "archivo") {
            const u = v.url || "";
            const fullUrl = u.startsWith("http") ? u : `${apiOrigin}${u.startsWith("/") ? "" : "/"}${u}`;
            arch.push({
              id: vid,
              url: fullUrl,
              titulo,
              existente: true,
              preview: fullUrl,
            });
          } else {
            yt.push({ id: vid, url: v.url, titulo, tipo: "youtube" });
          }
        }
        setVideosArchivo(arch);
        setVideosYoutube(yt);
      } catch {
        setVideosArchivo([]);
        setVideosYoutube([]);
      }
      try {
        const r = await api.get("/propiedades/" + id + "/documentos");
        const apiOrigin = (import.meta.env.VITE_API_URL || "http://localhost:4000").replace(/\/$/, "");
        setDocumentos(
          (r.data.data || []).map((d) => {
            const u = d.url || "";
            const fullUrl = u.startsWith("http") ? u : `${apiOrigin}${u.startsWith("/") ? "" : "/"}${u}`;
            return {
              id: d.id,
              existente: true,
              url: fullUrl,
              preview: fullUrl,
              nombre_documento: d.nombre_documento || d.nombre_original || "Documento",
              tipo_codigo: d.tipo_codigo || "documento_generico",
              descripcion_tipo: d.descripcion_tipo || "",
              mime: d.mime || "",
              fecha: d.created_at,
            };
          })
        );
      } catch {
        setDocumentos([]);
      }
      setDocumentosEliminar([]);
      let propRows = [];
      let contRows = [];
      try {
        const r = await api.get("/propiedades/" + id + "/propietarios");
        propRows = r.data.data || [];
        setPropietarios(propRows.map((p) => ({ ...p, id: p.id || crypto.randomUUID() })));
      } catch {}
      try {
        const r = await api.get("/propiedades/" + id + "/contactos");
        contRows = r.data.data || [];
        setContactosAsociados(contRows.map((x) => ({ ...x, id: x.id || crypto.randomUUID() })));
      } catch {}
      // Si el modal agente guardó titular en datos_extras pero aún no hay filas CRM, hidratar.
      if (data.captacion_vinculada_id && (propRows.length === 0 || contRows.length === 0)) {
        try {
          const capRes = await api.get(`/captaciones/${data.captacion_vinculada_id}`);
          const exCap = capRes.data?.data?.datos_extras;
          const extrasCap = exCap && typeof exCap === "object" ? exCap : {};
          if (propRows.length === 0) {
            const fichaProps = propietariosFichaDesdeExtras(extrasCap);
            if (fichaProps.length > 0) {
              setPropietarios(
                fichaProps.map((p) => ({
                  ...p,
                  id: p.id || globalThis.crypto?.randomUUID?.() || `p-${Math.random().toString(36).slice(2)}`,
                })),
              );
            }
          }
          if (contRows.length === 0) {
            const fichaCont = contactosAsociadosFichaDesdeExtras(extrasCap);
            if (fichaCont.length > 0) {
              setContactosAsociados(
                fichaCont.map((c) => ({
                  ...c,
                  id: c.id || globalThis.crypto?.randomUUID?.() || `c-${Math.random().toString(36).slice(2)}`,
                })),
              );
            }
          }
        } catch { /* ignore */ }
      }
      const comId =
        data.contacto_comunicacion_id ||
        propRows.find((p) => p.es_comunicacion)?.contacto_id ||
        contRows.find((c) => c.es_comunicacion)?.contacto_id ||
        null;
      setContactoComunicacionId(comId);
    } catch (err) {
      setError("Error al cargar la propiedad: " + textoErrorApi(err.response?.data?.error ?? err.response?.data ?? err.message, err.message));
    } finally {
      setFichaCargada(true);
      setCargando(false);
    }
  };

  // Al abrir «Empezar nueva» / Nueva captación en blanco: no arrastrar el id del borrador anterior.
  useEffect(() => {
    if (esEdicion) return;
    if (fromStateCaptacion?.id || embed?.embeddedCaptacion?.id) {
      captacionAltaBlancoIdRef.current = null;
      return;
    }
    if (!entradaDirectaNueva && !pendingNuevaCaptacion) return;
    captacionAltaBlancoIdRef.current = null;
    captacionOrigenRef.current = null;
    setCaptacionFromRecovery(null);
    clearFichaCaptacionSession();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cap = captacionOrigen;
    if (cap?.id) {
      if (fromStateCaptacion?.id) setCaptacionFromRecovery(null);
      try {
        sessionStorage.setItem(SS_FICHA_CAP_ID, cap.id);
      } catch {
        /* ignore */
      }
    }
  }, [captacionOrigen?.id, fromStateCaptacion?.id]);

  useEffect(() => {
    if (esEdicion) return;
    if (captacionOrigen?.id) return;
    if (location.state?.pendingNuevaCaptacion || location.state?.altaCaptacionDesdePipeline) return;
    if (captacionFromRecovery?.id) return;

    let cancelled = false;
    (async () => {
      let recovered = false;
      try {
        const sid = sessionStorage.getItem(SS_FICHA_CAP_ID);
        if (!sid) {
          setCargando(false);
          return;
        }
        const res = await api.get(`/captaciones/${sid}`);
        const row = res.data?.data;
        if (cancelled) return;
        if (!row?.id) {
          clearFichaCaptacionSession();
          setCargando(false);
          return;
        }
        const etapa = String(row.etapa || "");
        if (!["visita_cap", "contactado_cap"].includes(etapa)) {
          clearFichaCaptacionSession();
          setCargando(false);
          return;
        }
        setCaptacionFromRecovery(row);
        recovered = true;
      } catch {
        clearFichaCaptacionSession();
        setCargando(false);
      } finally {
        if (!cancelled && !recovered) setCargando(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    esEdicion,
    captacionOrigen?.id,
    location.state?.pendingNuevaCaptacion,
    location.state?.altaCaptacionDesdePipeline,
    captacionFromRecovery?.id,
    pendingNuevaCaptacion,
  ]);

  useEffect(() => {
    cargarAuxiliares();
  }, []);

  useEffect(() => {
    if (esEdicion) {
      cargarPropiedad();
      return;
    }
    if (captacionOrigen?.id) {
      (async () => {
        setCargando(true);
        try {
          await precargarDesdeCaptacion(captacionOrigen);
        } finally {
          setCargando(false);
        }
      })();
      return;
    }
  }, [esEdicion, id, captacionOrigen?.id, pendingNuevaCaptacion]);

  const cambiar = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (camposError.includes(name)) setCamposError(p => p.filter(x => x !== name));
  };
  const cambiarDirecto = (name, value) => {
    setForm(p => ({ ...p, [name]: value }));
    if (camposError.includes(name)) setCamposError(p => p.filter(x => x !== name));
  };
  const cambiarVarios = (patch) => {
    if (!patch || typeof patch !== "object") return;
    const keys = Object.keys(patch);
    setForm((p) => ({ ...p, ...patch }));
    if (keys.some((k) => camposError.includes(k))) {
      setCamposError((p) => p.filter((x) => !keys.includes(x)));
    }
  };
  const actualizarAuxiliar = useCallback((tabla, data) => {
    setAuxiliares((prev) => ({ ...prev, [tabla]: data }));
  }, []);

  const scrollToSection = (sid) => {
    setSeccionActiva(sid);
    if (sid === "cabecera") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.getElementById("seccion-" + sid)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const irAPrecioVenta = () => {
    setSeccionActiva("internos");
    setFocusPrecioTick((t) => t + 1);
  };

  // Desde «Llaves fuera» → Abrir: Internos desplegado + foco en Tenemos llaves / Han vuelto.
  useEffect(() => {
    if (!fichaCargada || !focusLlavesPendingRef.current) return;
    focusLlavesPendingRef.current = false;
    setSeccionActiva("internos");
    setFocusLlavesTick((t) => t + 1);
  }, [fichaCargada]);

  /**
   * Desde Fallos de sync: enfoca el campo que provoca el error.
   * Códigos mapper / heurística por texto del motivo.
   */
  const focusCampoPortalSync = useCallback((fix, formSnap) => {
    if (!fix) return;
    const code = String(fix.code || "").toUpperCase();
    const errTxt = String(fix.error || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const tipoNombre = String(
      formSnap?.tipo_nombre
      || formSnap?.tipo
      || formSnap?._tipoNombrePortalSync
      || ""
    ).toLowerCase();
    const esSolar = /solar|parcela|terreno|land/.test(tipoNombre);

    let seccion = "cabecera";
    let campoId = null;

    if (code === "FOTOCASA_PRO_NO_PRICE" || /(precio|price)/.test(errTxt)) {
      seccion = "internos";
      campoId = "campo-precio-venta";
      setSeccionActiva(seccion);
      setFocusPrecioTick((t) => t + 1);
      return;
    }
    if (
      code === "FOTOCASA_PRO_NO_COORDS"
      || /coordenad|latitud|longitud|ubicaci|gps/.test(errTxt)
    ) {
      seccion = "localizacion";
      campoId = "campo-coords-gps";
    } else if (/faltan \d+ datos en la ficha:/i.test(errTxt)) {
      // Listado completo Idealista: ir al primer faltante (1) …).
      const mFirst = errTxt.match(/1\)\s*([^;]+)/);
      const first = String(mFirst ? mFirst[1] : "").toLowerCase();
      if (/t[ií]tulo/.test(first)) {
        seccion = "publicacion";
        campoId = "campo-web_titulo";
      } else if (/poblaci/.test(first)) {
        seccion = "localizacion";
        campoId = "campo-poblacion";
      } else if (/m²|m2|metro/.test(first)) {
        seccion = "generales";
        campoId = esSolar ? "campo-m2_parcela" : "campo-m2_construidos";
      } else if (/descripci/.test(first)) {
        seccion = "publicacion";
        campoId = "campo-web_desc_larga_es";
      } else if (/coordenad|mapa/.test(first)) {
        seccion = "localizacion";
        campoId = "campo-coords-gps";
      } else if (/precio/.test(first)) {
        seccion = "cabecera";
        campoId = "campo-precio_venta";
      } else {
        seccion = "localizacion";
        campoId = "campo-codigo_postal";
      }
    } else if (
      /codigo postal|c\.?\s*p\.?|postalcode|cp valido|5 numeros/.test(errTxt)
    ) {
      seccion = "localizacion";
      campoId = "campo-codigo_postal";
    } else if (/poblacion|town|municipio/.test(errTxt)) {
      seccion = "localizacion";
      campoId = "campo-poblacion";
    } else if (/t[ií]tulo web|web_titulo|titulo del inmueble/.test(errTxt)) {
      seccion = "publicacion";
      campoId = "campo-web_titulo";
    } else if (/nombre de la via|direccion|street|calle/.test(errTxt) && !/postal/.test(errTxt)) {
      seccion = "localizacion";
      campoId = "campo-nombre_via";
    } else if (
      code === "FOTOCASA_PRO_NO_AREA"
      || /m²|m2|featureid\s*1|superficie|area constructed|area plot/.test(errTxt)
    ) {
      seccion = "generales";
      campoId = esSolar ? "campo-m2_parcela" : "campo-m2_construidos";
    } else if (/descripcion/.test(errTxt)) {
      seccion = "publicacion";
      campoId = "campo-web_desc_larga_es";
    } else if (/not allowed for studio|estudio.*dormitor|studio.*rooms|rooms.*studio/.test(errTxt)) {
      seccion = "cabecera";
      campoId = "campo-tipo_inmueble";
    } else if (/tipo de inmueble|\btype\b/.test(errTxt)) {
      seccion = "cabecera";
      campoId = "campo-tipo_inmueble";
    } else if (code === "FOTOCASA_PRO_NO_REF" || /referencia|externalid/.test(errTxt)) {
      seccion = "cabecera";
      campoId = "campo-referencia";
    } else if (code === "IDEALISTA_INCOMPLETE" || code === "IDEALISTA_PUBLISH_CAP") {
      // Varios faltantes: priorizar el primero del listado (1) …); si no, CP.
      const mFirst = errTxt.match(/1\)\s*([^;]+)/);
      const first = mFirst ? mFirst[1] : "";
      if (/t[ií]tulo web/.test(first) || (/t[ií]tulo web/.test(errTxt) && !/c[oó]digo postal/.test(errTxt))) {
        seccion = "publicacion";
        campoId = "campo-web_titulo";
      } else if (/poblaci/.test(first)) {
        seccion = "localizacion";
        campoId = "campo-poblacion";
      } else if (code === "IDEALISTA_PUBLISH_CAP") {
        seccion = "cabecera";
        campoId = null;
      } else {
        seccion = "localizacion";
        campoId = "campo-codigo_postal";
      }
    } else if (code === "CRM_ACTIVO_SIN_REMOTO") {
      seccion = "cabecera";
    }

    setSeccionActiva(seccion);

    let intentos = 0;
    const scrollAndFocus = () => {
      intentos += 1;
      const input = campoId ? document.getElementById(campoId) : null;
      const sec = document.getElementById("seccion-" + seccion);
      const target = input || sec;
      if (!target) {
        if (intentos < 20) window.setTimeout(scrollAndFocus, 100);
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      if (input) {
        try {
          // Aunque esté disabled, intentar foco; si no, al menos queda visible.
          input.focus({ preventScroll: true });
        } catch { /* ignore */ }
        try {
          input.style.outline = "2px solid #3b82f6";
          input.style.outlineOffset = "2px";
          window.setTimeout(() => {
            if (input) {
              input.style.outline = "";
              input.style.outlineOffset = "";
            }
          }, 2500);
        } catch { /* ignore */ }
      }
    };
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollAndFocus);
    });
    window.setTimeout(scrollAndFocus, 200);
    window.setTimeout(scrollAndFocus, 600);
  }, []);

  /** Activa el portal del fallo (encola sync; no debe colgar el «Actualizar»). */
  const publicarPortalTrasCorregirFallo = async () => {
    const fix = portalSyncFixRef.current;
    if (!fix) return { ok: true, skipped: true };
    const propiedadId = String(fix.propiedadId || id || "").trim();
    if (!propiedadId) return { ok: false, error: "Sin propiedad" };

    let portalId = fix.portalId;
    const slug = String(fix.portalSlug || "").toLowerCase();
    if (!portalId && slug) {
      const res = await api.get("/portales", { timeout: 8000 });
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      portalId = list.find((p) => String(p.slug || "").toLowerCase() === slug)?.id || null;
    }
    if (!portalId) {
      return { ok: false, error: `No se encontró el portal ${fix.portalNombre || slug || ""}` };
    }

    const res = await api.post(
      "/portales/propiedades/toggle",
      {
        propiedad_id: propiedadId,
        portal_id: portalId,
        activo: true,
      },
      { timeout: 15000 }
    );
    return {
      ok: true,
      async: Boolean(res.data?.async),
      portal: fix.portalNombre || slug,
      fotocasa: res.data?.fotocasa,
      pisos: res.data?.pisos,
      idealista: res.data?.idealista,
    };
  };

  useEffect(() => {
    if (!esEdicion || !fichaCargada || cargando) return;
    if (portalSyncFocusDoneRef.current) return;
    const fix = portalSyncFixRef.current;
    if (!fix) return;

    portalSyncFocusDoneRef.current = true;
    const tipos = auxiliares["tipo-inmueble"] || auxiliares.tipo_inmueble || [];
    const tipo = tipos.find((t) => String(t.id) === String(form.tipo_inmueble_id));
    // Esperar un poco más: secciones/permisos tardan en montar tras fichaCargada
    const t = window.setTimeout(() => {
      focusCampoPortalSync(fix, {
        ...form,
        _tipoNombrePortalSync: tipo?.nombre || "",
      });
    }, 350);
    return () => window.clearTimeout(t);
  }, [esEdicion, fichaCargada, cargando, form.tipo_inmueble_id, auxiliares, focusCampoPortalSync]);

  /** Fusiona el estado del formulario en datos_extras (borrador: persiste valores actuales). */
  const mergeFormIntoCaptacionExtras = (prevExtras, f) => {
    const datos = { ...prevExtras };
    const webTitulo = String(f.web_titulo ?? "").trim();
    if (webTitulo) {
      datos.web_titulo = webTitulo;
      datos.titulo = webTitulo;
    }
    for (const [k, v] of Object.entries(f)) {
      if (typeof v === "boolean") {
        datos[k] = v;
        continue;
      }
      if (v === null || v === undefined) continue;
      if (k === "eq_observaciones" || k === "info_visita" || k === "observaciones_internas") {
        datos[k] = String(v ?? "");
        continue;
      }
      if (typeof v === "number" && !Number.isNaN(v)) {
        datos[k] = v;
        continue;
      }
      if (Array.isArray(v)) {
        datos[k] = v;
        continue;
      }
      if (typeof v === "object") continue;
      // No pisar descripción/título web ya guardados con un string vacío (autosave stale).
      if (
        (k === "web_desc_larga_es" || k === "web_desc_corta_es" || k.startsWith("web_desc_")
          || k === "web_titulo" || k === "titulo")
        && String(v).trim() === ""
        && String(datos[k] || "").trim()
      ) {
        continue;
      }
      datos[k] = v;
    }
    return datos;
  };

  /**
   * Resuelve qué captación usar al guardar.
   * Orden de prioridad:
   *   1. captacionOrigen (derivado de location.state / captacionFromRecovery)
   *   2. captación creada en esta alta en blanco
   *   3. captacionOrigenRef (persiste aunque location.state se pierda entre renders)
   *   4. sessionStorage → GET al backend (nunca al forzar alta en blanco sin id propio)
   */
  const resolverCaptacionAlGuardar = async () => {
    if (captacionOrigen?.id) return captacionOrigen;
    if (captacionAltaBlancoIdRef.current) {
      try {
        const res = await api.get(`/captaciones/${captacionAltaBlancoIdRef.current}`);
        const row = res.data?.data;
        if (row?.id) return row;
      } catch {
        /* ignore */
      }
    }
    if (captacionOrigenRef.current?.id) return captacionOrigenRef.current;
    // Alta en blanco: no reutilizar el borrador anterior de sessionStorage.
    if (entradaDirectaNueva && !fromStateCaptacion?.id) return null;
    try {
      const sid = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SS_FICHA_CAP_ID) : null;
      if (!sid) return null;
      if (captacionAltaBlancoIdRef.current && sid !== captacionAltaBlancoIdRef.current) return null;
      const res = await api.get(`/captaciones/${sid}`);
      const row = res.data?.data;
      if (!row?.id) return null;
      const etapa = String(row.etapa || "");
      if (!["visita_cap", "contactado_cap", "precaptacion"].includes(etapa)) return null;
      return row;
    } catch {
      return null;
    }
  };

  /** Crea la captación en CRM solo al guardar (no al abrir «+ Nueva captación»). */
  const crearCaptacionNuevaSiNecesaria = async () => {
    const existente = await resolverCaptacionAlGuardar();
    if (existente?.id) return existente;
    const esAltaNueva =
      pendingNuevaCaptacion ||
      location.state?.entradaDirectaNueva ||
      location.state?.pendingNuevaCaptacion;
    if (!esAltaNueva) return null;

    const fd = new FormData();
    fd.append("entrada_directa", "true");
    const res = await api.post("/captaciones", fd);
    const cap = res.data?.data;
    if (!cap?.id) throw new Error("No se creó la captación");
    const full = await api.get(`/captaciones/${cap.id}`);
    const row = full.data?.data;
    if (!row?.id) throw new Error("Captación no encontrada");

    captacionAltaBlancoIdRef.current = row.id;
    captacionOrigenRef.current = row;
    setCaptacionFromRecovery(row);
    try {
      sessionStorage.setItem(SS_FICHA_CAP_ID, row.id);
    } catch {
      /* ignore */
    }
    return row;
  };

  const enFichaCaptacion = !esEdicion && !!(
    pendingNuevaCaptacion ||
    captacionOrigen?.id ||
    captacionOrigenRef.current?.id ||
    fromCapIdEarly ||
    maybeRecoverCaptacion
  );

  /**
   * Tras generar/traducir IA: escribir ya en datos_extras de la captación vinculada.
   * Debe funcionar también en etapa revisar_cap / edición de propiedad (antes solo ficha previa).
   */
  const persistirTextosWebCaptacionYa = async (updates) => {
    if (!updates || !Object.keys(updates).length) return;
    const capId =
      captacionOrigen?.id ||
      captacionOrigenRef.current?.id ||
      captacionVinculadaId ||
      fromCapIdEarly ||
      (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SS_FICHA_CAP_ID) : null);
    if (!capId) return;
    try {
      formRef.current = { ...(formRef.current || {}), ...updates };
      await api.patch(`/captaciones/${capId}/datos-extras`, {
        datos: updates,
        solo_borrador: true,
      });
      const prevEx =
        typeof captacionOrigenRef.current?.datos_extras === "object" &&
        captacionOrigenRef.current?.datos_extras !== null
          ? captacionOrigenRef.current.datos_extras
          : {};
      if (captacionOrigenRef.current?.id) {
        captacionOrigenRef.current = {
          ...captacionOrigenRef.current,
          datos_extras: { ...prevEx, ...updates },
        };
      } else {
        captacionOrigenRef.current = { id: capId, datos_extras: { ...updates } };
      }
    } catch (err) {
      console.warn("[PropiedadForm] persist textos web captación:", err?.message || err);
    }
  };

  const captacionIdActiva =
    captacionOrigen?.id ||
    captacionOrigenRef.current?.id ||
    captacionFromRecovery?.id ||
    fromCapIdEarly ||
    null;

  const subidaFondo = useCaptacionSubidaFondo({
    captacionId: captacionIdActiva,
    enabled: enFichaCaptacion && !!captacionIdActiva,
    fotos,
    setFotos,
    documentos,
    setDocumentos,
    videosArchivo,
    setVideosArchivo,
    documentosEliminar,
    setDocumentosEliminar,
    idsReportajeInicialRef,
  });

  const construirDatosCaptacion = (capWork) => {
    const formNow = formRef.current;
    const propietariosNow = propietariosRef.current;
    const superficiesNow = superficiesRef.current;
    const contactosNow = contactosAsociadosRef.current;
    const contactoComIdNow = contactoComunicacionIdRef.current;
    const prevExtras =
      typeof capWork.datos_extras === "object" && capWork.datos_extras !== null
        ? { ...capWork.datos_extras }
        : (() => {
            try {
              return JSON.parse(capWork.datos_extras || "{}");
            } catch {
              return {};
            }
          })();
    let datos = mergeFormIntoCaptacionExtras(prevExtras, formNow);
    if (propietariosNow.length > 0) {
      const p0 = propietariosNow[0];
      const label = [p0.nombre, p0.apellidos].filter(Boolean).join(" ").trim();
      if (label) datos = { ...datos, propietario: label };
      const comIdStr = contactoComIdNow != null ? String(contactoComIdNow) : "";
      const comKeyResolved = (() => {
        if (!comIdStr) return prevExtras.comunicacion_key || "titular";
        const hitP = propietariosNow.find(
          (p) =>
            (p.contacto_id && String(p.contacto_id) === comIdStr) ||
            (p.id != null && String(p.id) === comIdStr)
        );
        if (hitP?.id != null && (String(hitP.id) === "titular" || /^prop:/.test(String(hitP.id)))) {
          return String(hitP.id);
        }
        const hitC = contactosNow.find(
          (c) =>
            (c.contacto_id && String(c.contacto_id) === comIdStr) ||
            (c.id != null && String(c.id) === comIdStr)
        );
        if (hitC?.id != null && /^cont:/.test(String(hitC.id))) return String(hitC.id);
        if (/^(titular|prop:\d+|cont:\d+)$/.test(comIdStr)) return comIdStr;
        return prevExtras.comunicacion_key || "titular";
      })();
      datos = {
        ...datos,
        comunicacion_key: comKeyResolved,
        propietarios_ficha: propietariosNow.map((p) => {
          const isCom =
            (p.contacto_id && String(p.contacto_id) === comIdStr) ||
            (p.id != null && String(p.id) === comIdStr) ||
            String(p.id) === String(comKeyResolved);
          return { ...p, comunicacion: !!isCom, contacto_principal: !!isCom };
        }),
      };
      const tit = propietariosNow.find((p) => String(p.id) === "titular") || propietariosNow[0];
      if (tit) datos = extrasDomicilioTitularDesdePersona(tit, datos);
    } else {
      datos = { ...datos, propietarios_ficha: [] };
    }
    const comIdStr2 = contactoComIdNow != null ? String(contactoComIdNow) : "";
    if (!datos.comunicacion_key && comIdStr2) {
      const hitC = contactosNow.find(
        (c) =>
          (c.contacto_id && String(c.contacto_id) === comIdStr2) ||
          (c.id != null && String(c.id) === comIdStr2)
      );
      if (hitC?.id != null && /^cont:/.test(String(hitC.id))) {
        datos = { ...datos, comunicacion_key: String(hitC.id) };
      } else if (/^(titular|prop:\d+|cont:\d+)$/.test(comIdStr2)) {
        datos = { ...datos, comunicacion_key: comIdStr2 };
      } else if (prevExtras.comunicacion_key) {
        datos = { ...datos, comunicacion_key: prevExtras.comunicacion_key };
      }
    }
    datos = {
      ...datos,
      contactos_asociados_ficha: contactosNow.map((c) => {
        const isCom =
          (c.contacto_id && String(c.contacto_id) === comIdStr2) ||
          (c.id != null && String(c.id) === comIdStr2) ||
          (datos.comunicacion_key && String(c.id) === String(datos.comunicacion_key));
        return { ...c, comunicacion: !!isCom, contacto_principal: !!isCom };
      }),
    };
    datos = { ...datos, superficies_ficha: superficiesNow };
    if (contactoComIdNow && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(contactoComIdNow))) {
      datos = { ...datos, contacto_comunicacion_id: contactoComIdNow };
    } else {
      // Fuente de verdad hasta validar: comunicacion_key (checkbox). No arrastrar UUID obsoleto.
      datos = { ...datos, contacto_comunicacion_id: null };
    }
    return anexarEstadoMultimedia(datos);
  };

  const anexarEstadoMultimedia = (datos) => {
    const docsActivos = documentos.filter((d) => !documentosEliminar.includes(d.id));
    const pendiente =
      subidaFondo.stats.pendientes > 0 ||
      subidaFondo.stats.subiendo > 0 ||
      subidaFondo.stats.errores > 0 ||
      fotos.some((f) => f.file && f.subidaEstado !== "ok") ||
      docsActivos.some((d) => d.file && d.subidaEstado !== "ok") ||
      videosArchivo.some((v) => v.file && v.subidaEstado !== "ok");

    return {
      ...datos,
      // Nunca degradar móvil → escritorio (antes un extras incompleto borraba Todas/Marco en Para Revisar).
      origen_multimedia: (() => {
        const prev = String(datos.origen_multimedia || "").trim();
        if (prev === "movil") return "movil";
        if (
          datos.seleccion_marco_estado ||
          datos.prioridad_seleccion === "marco" ||
          datos.prioridad_seleccion === "todas"
        ) {
          return "movil";
        }
        if (prev === "escritorio") return "escritorio";
        return prev || "escritorio";
      })(),
      subida_multimedia_pendiente: pendiente,
      multimedia_esperada_fotos: fotos.length,
      multimedia_esperada_documentos: docsActivos.length,
      multimedia_esperada_videos: videosArchivo.length,
    };
  };

  const haySubidasEnCurso = () =>
    subidaFondo.stats.pendientes + subidaFondo.stats.subiendo > 0 ||
    fotos.some((f) => f.file && f.subidaEstado !== "ok") ||
    documentos
      .filter((d) => !documentosEliminar.includes(d.id))
      .some((d) => d.file && d.subidaEstado !== "ok") ||
    videosArchivo.some((v) => v.file && v.subidaEstado !== "ok");

  /**
   * @param {{ finalizar?: boolean, silent?: boolean, capExplicit?: object|null }} opts
   */
  const persistirCaptacionFicha = async ({
    finalizar = false,
    silent = false,
    capExplicit = null,
  } = {}) => {
    const capExplicitResolved = capExplicit || captacionOrigen || captacionOrigenRef.current;
    let cap = capExplicitResolved;
    if (!cap?.id) {
      cap = await resolverCaptacionAlGuardar();
    }
    if (!cap?.id && !silent) {
      cap = await crearCaptacionNuevaSiNecesaria();
    }
    if (!cap?.id) return false;
    if (guardandoBorradorRef.current && silent) return false;
    if (!silent) {
      if (finalizar) {
        setError("");
        setFinalizando(true);
      } else {
        setError("");
        setEnviando(true);
      }
    } else {
      guardandoBorradorRef.current = true;
      setAutosaveActivo(true);
    }
    try {
      let capWork = cap;
      if (!captacionOrigen?.id && !captacionOrigenRef.current?.id) {
        try {
          const ref = await api.get(`/captaciones/${cap.id}`);
          if (ref.data?.data?.id) capWork = ref.data.data;
        } catch { /* usar cap recibido */ }
      }

      if (captacionIdActiva && (!silent || finalizar)) {
        await subidaFondo.sincronizarMetadatos();
        idsReportajeInicialRef.current = new Set(
          fotos
            .filter((f) => f.existente && f.id && /^[0-9a-f-]{36}$/i.test(String(f.id)))
            .map((f) => f.id),
        );
      }

      let datos = construirDatosCaptacion(capWork);

      const extrasCap =
        (typeof capWork.datos_extras === "object" && capWork.datos_extras) || {};
      const esAltaDirectaCartera =
        pendingNuevaCaptacion
        || extrasCap.entrada_directa === true
        || extrasCap.entrada_directa === "true"
        || location.state?.entradaDirectaNueva;

      if (finalizar) {
        if (haySubidasEnCurso()) {
          datos = {
            ...datos,
            ficha_finalizada_pendiente_multimedia: true,
            subida_multimedia_pendiente: true,
          };
          subidaFondo.prepararFinalizarConSubidasPendientes();
          await api.patch(`/captaciones/${capWork.id}/datos-extras`, {
            datos,
            solo_borrador: true,
          });
          captacionOrigenRef.current = { ...capWork, datos_extras: datos };
          if (esAltaDirectaCartera) {
            setError(
              "Espera a que terminen de subirse las fotos/documentos antes de dar de alta en Captaciones."
            );
            window.scrollTo({ top: 0, behavior: "smooth" });
            return false;
          }
          clearFichaCaptacionSession();
          sessionStorage.setItem(
            "vitalcasa_msg",
            "Ficha guardada. Las fotos y documentos siguen subiendo en segundo plano; la captación pasará a Para Revisar cuando terminen."
          );
          navigateFormExit();
          return true;
        }

        datos = {
          ...datos,
          subida_multimedia_pendiente: false,
          ficha_finalizada_pendiente_multimedia: false,
          entrada_directa: esAltaDirectaCartera ? true : datos.entrada_directa,
        };
      }

      try {
        await api.patch(`/captaciones/${capWork.id}/datos-extras`, {
          datos,
          solo_borrador: true,
        });
      } catch (patchErr) {
        throw patchErr;
      }
      captacionOrigenRef.current = { ...capWork, datos_extras: datos };

      if (finalizar && esAltaDirectaCartera) {
        try {
          const alta = await api.post(`/captaciones/${capWork.id}/alta-directa-cartera`, {
            datos,
          });
          clearFichaCaptacionSession();
          const ref = alta.data?.referencia || "";
          sessionStorage.setItem(
            "vitalcasa_msg",
            ref
              ? `Alta en Captaciones (${ref}). Marco ha iniciado el proceso.`
              : "Alta en Captaciones. Marco ha iniciado el proceso."
          );
          navigate("/pipeline?modo=captaciones");
          return true;
        } catch (altaErr) {
          const status = altaErr.response?.status;
          const data = altaErr.response?.data || {};
          const msg = textoErrorApi(
            data.error ?? data,
            "No se pudo dar de alta en Captaciones"
          );
          try {
            console.error("[PropiedadForm] alta-directa-cartera falló", status, data, altaErr);
          } catch {
            /* ignore */
          }
          if (status === 422 || status === 400 || status === 409 || !status) {
            const faltan = Array.isArray(data.camposFaltantes) ? data.camposFaltantes : [];
            const labels = faltan.map((f) =>
              typeof f === "string" ? f : (f.campo_label || f.campo_key || String(f))
            );
            const esRefOcupada =
              data.codigo === "referencia_ocupada" || /referencia/i.test(msg);
            if (labels.length) {
              setModalCamposFaltantes(labels);
            } else {
              setModalCamposFaltantes([msg]);
            }
            setModalFaltantesModo("alta_directa");
            setMostrarModalRevisar(true);
            setError(msg);
            setBorradorGuardadoAt(Date.now());
            if (esRefOcupada) {
              setCamposError((prev) => (prev.includes("referencia") ? prev : [...prev, "referencia"]));
              setSeccionActiva("cabecera");
              setForm((p) => ({ ...p, referencia_manual: false, referencia: "" }));
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
            return false;
          }
          // Error inesperado (red/5xx): un solo aviso DOM
          mostrarAvisoVitalCasa(msg, { titulo: "Error al finalizar" });
          throw altaErr;
        }
      }

      if (finalizar) {
        // Flujo normal (visita/móvil): avanzar a Para Revisar si todo OK
        try {
          await api.patch(`/captaciones/${capWork.id}/datos-extras`, {
            datos,
            solo_borrador: false,
          });
        } catch (patchErr) {
          if (patchErr.response?.status === 422) {
            const msg = textoErrorApi(
              patchErr.response?.data?.error ?? patchErr.response?.data,
              "No se puede pasar a Para Revisar todavía"
            );
            setError(msg);
            await api.patch(`/captaciones/${capWork.id}/datos-extras`, {
              datos: { ...datos, subida_multimedia_pendiente: true },
              solo_borrador: true,
            }).catch(() => {});
            captacionOrigenRef.current = { ...capWork, datos_extras: datos };
            setBorradorGuardadoAt(Date.now());
            return false;
          }
          throw patchErr;
        }
        clearFichaCaptacionSession();
        sessionStorage.setItem(
          "vitalcasa_msg",
          "Ficha guardada. La captación ha pasado a Para Revisar."
        );
        navigateFormExit();
        return true;
      }
      setBorradorGuardadoAt(Date.now());
      if (!silent && !finalizar) {
        sessionStorage.setItem("vitalcasa_msg", "Borrador guardado. Puedes seguir editando la ficha.");
      }
      return true;
    } catch (err) {
      if (!silent) {
        const msg = textoErrorApi(err.response?.data?.error ?? err.response?.data ?? err.message, "Error al guardar la ficha en la captación");
        setError(msg);
        if (finalizar) {
          mostrarAvisoVitalCasa(msg, { titulo: "Error al finalizar" });
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return false;
    } finally {
      if (!silent) {
        setEnviando(false);
        setFinalizando(false);
      }
      guardandoBorradorRef.current = false;
      setAutosaveActivo(false);
    }
  };

  const guardarFichaEnCaptacion = (capExplicit = null) =>
    persistirCaptacionFicha({ finalizar: true, capExplicit });

  const guardarBorradorCaptacion = ({ silent = false } = {}) =>
    persistirCaptacionFicha({ finalizar: false, silent });

  // Ampliar: al salir de /propiedades/nueva (Completar campos → Ver ficha) también flush.
  useEffect(() => {
    if (!enFichaCaptacion || !captacionIdActiva) return undefined;
    const flush = () => {
      if (omitirFlushAlSalirRef.current) return;
      try {
        void persistirCaptacionFicha({ finalizar: false, silent: true });
      } catch { /* ignore */ }
    };
    const onHide = () => flush();
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      flush();
    };
  }, [enFichaCaptacion, captacionIdActiva]); // eslint-disable-line react-hooks/exhaustive-deps

  // Overlay pipeline: registrar flush para «Cerrar ficha» (antes se perdían cambios).
  useEffect(() => {
    if (!embed?.registerFlushSave) return undefined;
    if (!enFichaCaptacion) {
      embed.registerFlushSave(null);
      return () => embed.registerFlushSave?.(null);
    }
    embed.registerFlushSave(() => {
      if (omitirFlushAlSalirRef.current) return Promise.resolve(false);
      return persistirCaptacionFicha({ finalizar: false, silent: true });
    });
    return () => embed.registerFlushSave?.(null);
  }, [embed, enFichaCaptacion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enFichaCaptacion || !captacionIdActiva) return undefined;
    const snapshot = JSON.stringify({
      form,
      propietarios,
      superficies,
      contactosAsociados,
      contactoComunicacionId,
    });
    if (snapshot === autosaveSnapshotRef.current) return undefined;
    clearTimeout(autosaveTimerRef.current);
    // Antes 45s: al cerrar «Ver ficha completa» se perdían Observaciones y el resto.
    autosaveTimerRef.current = setTimeout(() => {
      guardarBorradorCaptacion({ silent: true }).then((ok) => {
        if (ok) autosaveSnapshotRef.current = snapshot;
      });
    }, 2500);
    return () => clearTimeout(autosaveTimerRef.current);
  }, [
    enFichaCaptacion,
    captacionIdActiva,
    form,
    propietarios,
    superficies,
    contactosAsociados,
    contactoComunicacionId,
  ]);

  /**
   * Solo estado multimedia — no reenviar ficha completa.
   * Antes construía todo el payload con closure stale y pisaba superficies_ficha: [].
   */
  useEffect(() => {
    if (!enFichaCaptacion || !captacionIdActiva) return undefined;
    const cap = captacionOrigenRef.current;
    if (!cap?.id) return undefined;
    const timer = setTimeout(() => {
      if (guardandoBorradorRef.current || finalizando) return;
      const datos = anexarEstadoMultimedia({});
      api
        .patch(`/captaciones/${cap.id}/datos-extras`, { datos, solo_borrador: true })
        .then(() => {
          const prev =
            typeof captacionOrigenRef.current?.datos_extras === "object" &&
            captacionOrigenRef.current?.datos_extras !== null
              ? captacionOrigenRef.current.datos_extras
              : {};
          captacionOrigenRef.current = {
            ...(captacionOrigenRef.current || cap),
            datos_extras: { ...prev, ...datos },
          };
        })
        .catch(() => {});
    }, 2500);
    return () => clearTimeout(timer);
  }, [
    enFichaCaptacion,
    captacionIdActiva,
    subidaFondo.stats,
    fotos.length,
    documentos.length,
    videosArchivo.length,
    documentosEliminar.length,
    finalizando,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const archivosSubiendo =
    subidaFondo.stats.pendientes + subidaFondo.stats.subiendo > 0;
  const archivosConError = subidaFondo.stats.errores > 0;

  const appendPropiedadFormData = (fd, { incluirFotosOrden = true } = {}) => {
    Object.entries(form).forEach(([k, val]) => {
      if (FORM_OMITIR_EN_MULTIPART.has(k) || k.startsWith("hist_")) return;
      if (k === "contacto_comunicacion_id" || k === "updated_at") return;
      if (k === "poblacion" || k === "poblacion_nombre") return;
      if (typeof val === "object" && val !== null && !(val instanceof Date) && k !== "ub_vistas_ids" && k !== "garaje_tipos" && k !== "cartel_opciones" && k !== "garajes_plazas_detalle") return;
      if (typeof Blob !== "undefined" && val instanceof Blob) return;
      if (k === "referencia_manual") {
        fd.append(k, val ? "true" : "false");
        return;
      }
      if (k === "titulo") return;
      if (k === "web_titulo") {
        const t = String(val || "").trim();
        if (t) {
          fd.append("web_titulo", t);
          fd.append("titulo", t);
        }
        return;
      }
      if (k === "referencia" && !form.referencia_manual) return;
      if (k === "poblacion_id" && val != null && val !== "") {
        fd.append(k, normalizePoblacionId(val));
        return;
      }
      if (k === "ub_vistas_ids") {
        const ids = Array.isArray(val) ? val.map(String).filter(Boolean) : [];
        fd.append(k, JSON.stringify(ids));
        return;
      }
      if (k === "garaje_tipos") {
        const tipos = Array.isArray(val) ? val.map(String).filter(Boolean) : [];
        fd.append(k, JSON.stringify(tipos));
        return;
      }
      if (k === "garajes_plazas_detalle") {
        const det = Array.isArray(val) ? val : [];
        fd.append(k, JSON.stringify(det));
        return;
      }
      if (k === "cartel_opciones") {
        const opts = Array.isArray(val) ? val.map(String).filter(Boolean) : [];
        fd.append(k, JSON.stringify(opts));
        return;
      }
      if (k === "num_garaje" || k === "num_trastero") {
        if (val !== undefined && val !== null && String(val).trim() !== "") {
          fd.append(k, String(val).trim());
        }
        return;
      }
      if (k === "num_plazas_garaje") {
        if (val === 0 || val === "0" || (val != null && val !== "")) {
          fd.append(k, String(val));
        }
        return;
      }
      if (
        (k === "eq_observaciones" || k === "info_visita" || k === "observaciones_internas" || k === "precio_observaciones") &&
        val !== undefined && val !== null
      ) {
        fd.append(k, val);
        return;
      }
      if (val !== undefined && val !== null && val !== "") {
        // Booleanos siempre como "true"/"false" (FormData + multer).
        if (typeof val === "boolean") {
          fd.append(k, val ? "true" : "false");
          return;
        }
        fd.append(k, val);
      }
    });
    fd.append("superficies", JSON.stringify(superficies));
    fd.append("propietarios", JSON.stringify(propietarios));
    fd.append("contactos_asociados", JSON.stringify(contactosAsociados));
    const comId = contactoComunicacionId ? String(contactoComunicacionId).trim() : "";
    fd.append("contacto_comunicacion_id", comId);
    appendVideosToFormData(fd, videosArchivo, videosYoutube);
    appendDocumentosToFormData(fd, documentos, documentosEliminar);
    const fotosNuevas = fotos.filter((f) => f.file);
    fotosNuevas.forEach((f) => { fd.append("fotos", f.file); });
    fd.append(
      "fotos_meta",
      JSON.stringify(
        fotosNuevas.map((f) => ({
          tipo: f.tipo || "publica",
          descripcion_es: f.descripcion_es || "",
          orden: fotos.findIndex((x) => x.id === f.id),
          marco_autoenhance: f.marco_autoenhance !== false,
        }))
      )
    );
    if (incluirFotosOrden) {
      fd.append(
        "fotos_orden",
        JSON.stringify(fotos.filter((f) => f.existente && f.id).map((f) => f.id))
      );
    }
  };

  const enviar = async (e) => {
    e.preventDefault();
    setError(""); setCamposError([]);
    // Antes: return silencioso → Enter / submit no guardaba nada en ficha de captación.
    if (enFichaCaptacion) {
      await guardarBorradorCaptacion({ silent: false });
      return;
    }
    const v = (name) => { const val = form[name]; return val !== undefined && val !== null && String(val).trim() !== ""; };
    const err = [];
    if (!v("tipo_inmueble_id")) err.push("tipo_inmueble_id");
    if (!v("operacion")) err.push("operacion");
    if (!v("web_titulo")) err.push("web_titulo");
    // Ampliar: exclusiva → duración y vencimiento obligatorios.
    if (form.exclusiva === true || form.exclusiva === "true" || form.exclusiva === 1 || form.exclusiva === "1") {
      if (!v("exclusiva_duracion")) err.push("exclusiva_duracion");
      if (!v("exclusiva_hasta")) err.push("exclusiva_hasta");
    }
    if (err.length > 0 && !esEdicion) {
      setCamposError(err);
      const nombres = {
        tipo_inmueble_id: "Tipo inmueble",
        operacion: "Operación",
        web_titulo: "Título web",
        exclusiva_duracion: "Duración exclusiva",
        exclusiva_hasta: "Vencimiento exclusiva",
      };
      setModalCamposFaltantes(err.map(x => nombres[x] || x));
      setModalFaltantesModo("propiedad");
      setMostrarModalRevisar(true);
      return;
    }
    if (err.length > 0 && esEdicion) {
      setCamposError(err);
      const nombres = {
        tipo_inmueble_id: "Tipo inmueble",
        operacion: "Operación",
        web_titulo: "Título web",
        exclusiva_duracion: "Duración exclusiva",
        exclusiva_hasta: "Vencimiento exclusiva",
      };
      setError("Campos obligatorios: " + err.map(x => nombres[x] || x).join(", "));
      scrollToSection(err.includes("web_titulo") ? "publicacion" : "cabecera");
      return;
    }
    setEnviando(true);
    try {
      const fd = new FormData();
      appendPropiedadFormData(fd);
      if (esEdicion) {
        let res;
        let msgYaConfirmadoPorRecheck = false;
        try {
          res = await api.put("/propiedades/" + id, fd, { timeout: 90000 });
        } catch (putErr) {
          // Timeout/502 Railway: a veces el UPDATE ya commitió (título sí guardado).
          const raw =
            putErr.response?.data?.error ??
            putErr.response?.data ??
            putErr.message ??
            "";
          const pareceTimeout =
            /application failed to respond|timeout|ECONNABORTED|504|502/i.test(
              String(typeof raw === "string" ? raw : raw?.message || putErr.code || "")
            ) || putErr.code === "ECONNABORTED";
          if (pareceTimeout && id) {
            try {
              const check = await api.get("/propiedades/" + id, { timeout: 15000 });
              const remote = check.data?.data || check.data || {};
              const wantTit = String(form.web_titulo || form.titulo || "").trim();
              const gotTit = String(remote.web_titulo || remote.titulo || "").trim();
              const titOk = !wantTit || gotTit === wantTit;
              const remoteUpdated = remote.updated_at
                ? new Date(remote.updated_at).getTime()
                : 0;
              const reciente =
                remoteUpdated > 0 && Date.now() - remoteUpdated < 3 * 60 * 1000;
              if (titOk && reciente) {
                res = {
                  data: {
                    ok: true,
                    data: {
                      id,
                      updated_at: remote.updated_at,
                      referencia: remote.referencia,
                    },
                  },
                };
                msgYaConfirmadoPorRecheck = true;
                sessionStorage.setItem(
                  "vitalcasa_msg",
                  "Propiedad actualizada (el servidor tardó en confirmar; los datos ya están guardados)."
                );
              } else {
                throw putErr;
              }
            } catch (_) {
              throw putErr;
            }
          } else {
            throw putErr;
          }
        }
        setHistoricoPreciosTick((t) => t + 1);
        if (!msgYaConfirmadoPorRecheck) {
          sessionStorage.setItem("vitalcasa_msg", "Propiedad actualizada correctamente");
        }

        if (portalSyncFixRef.current) {
          const nombrePortal = portalSyncFixRef.current.portalNombre || "portal";
          try {
            const pub = await publicarPortalTrasCorregirFallo();
            if (!pub.ok) {
              sessionStorage.setItem(
                "vitalcasa_msg",
                `Propiedad actualizada. No se pudo encolar ${nombrePortal}: ${pub.error || "error"}. Revisa Fallos.`
              );
            } else if (pub.async) {
              sessionStorage.setItem(
                "vitalcasa_msg",
                `Propiedad actualizada. Publicando en ${nombrePortal} en segundo plano…`
              );
            } else {
              sessionStorage.setItem(
                "vitalcasa_msg",
                `Propiedad actualizada y publicada en ${nombrePortal}`
              );
            }
          } catch (e) {
            const data = e.response?.data || {};
            const msg = textoErrorApi(
              data.fotocasa_error
                || data.pisos_error
                || data.idealista_error
                || data.error
                || e.message,
              "Error al publicar en el portal"
            );
            // La ficha ya está guardada: no quedarse en la pantalla con el error rojo
            sessionStorage.setItem(
              "vitalcasa_msg",
              `Propiedad actualizada. ${msg} Revisa Fallos de sincronización.`
            );
          }
          portalSyncFixRef.current = null;
        }

        const bajada = res.data?.bajada_precio;
        const leadsBaj = res.data?.leads_bajada;
        if (bajada?.aplica && Array.isArray(leadsBaj) && leadsBaj.length > 0) {
          setBajadaFlow({
            leads: leadsBaj,
            bajada,
            referencia: form.referencia || res.data?.data?.referencia || "",
            propiedadId: res.data?.data?.id || id || form.id || null,
          });
          return;
        }
        const ahoraDisp =
          form.disponible === true ||
          form.disponible === "true" ||
          form.disponible === 1 ||
          form.disponible === "1";
        // Si venimos de Fallos, ya publicamos el portal concreto: no abrir modal de republicar.
        if (!portalSyncFixRef.current && disponibleAlCargarRef.current === false && ahoraDisp) {
          const items = await cargarItemsPublicacionesPrevias([id]);
          if (items.length) {
            setModalPortalesPrevios(items);
            disponibleAlCargarRef.current = true;
            return;
          }
        }
        disponibleAlCargarRef.current = ahoraDisp;
      } else {
        await api.post("/propiedades", fd);
        clearFichaCaptacionSession();
        sessionStorage.setItem("vitalcasa_msg", "Propiedad creada correctamente");
      }
      navigateFormExit();
    } catch (err) {
      setError(textoErrorApi(err.response?.data?.error ?? err.response?.data ?? err.message, "Error al guardar"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setEnviando(false);
    }
  };

  const confirmarGuardarRevisar = async () => {
    setMostrarModalRevisar(false);
    setEnviando(true);
    try {
      const fd = new FormData();
      appendPropiedadFormData(fd, { incluirFotosOrden: false });
      await api.post("/propiedades", fd);
      clearFichaCaptacionSession();
      sessionStorage.setItem(
        "vitalcasa_msg",
        "Ficha de propiedad creada con datos incompletos. Revisa y complétala en el listado de propiedades."
      );
      navigateFormExit();
    } catch (err) {
      setError(textoErrorApi(err.response?.data?.error ?? err.response?.data ?? err.message, "Error al guardar"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setEnviando(false);
    }
  };

  const pc = {
    form: soloLecturaIdioma ? formVista : form,
    cambiar, cambiarDirecto, cambiarVarios, auxiliares: auxiliaresVista, actualizarAuxiliar,
    camposError, onIrAPrecio: irAPrecioVenta, esEdicion, soloLecturaIdioma, idiomaVista,
    t: tLabel,
    tv: tvLabel,
  };
  const visorAddressContext = useMemo(
    () => ({
      poblacionNombre:
        (form.poblacion_id && nombrePoblacionPorId(form.poblacion_id)) ||
        form.poblacion_nombre ||
        (form.poblacion_id ? "" : form.poblacion) ||
        "",
    }),
    [form.poblacion_id, form.poblacion_nombre, form.poblacion],
  );

  const COMP_MAP = {
    cabecera:     SeccionCabecera,
    generales:    SeccionGenerales,
    distribucion: SeccionDistribucion,
    equipamiento: SeccionEquipamiento,
    ubicacion:    SeccionUbicacion,
    economicos:   SeccionEconomicos,
    alquiler:     SeccionAlquiler,
    localizacion: SeccionLocalizacion,
    multimedia:   SeccionMultimedia,
    publicacion:  SeccionPublicacionWeb,
    propietarios: SeccionPropietarios,
    internos:     SeccionDatosInternos,
    registro:     SeccionRegistro,
    notasimple:   SeccionNotaSimple,
    documentacion: SeccionDocumentacion,
    historial:     SeccionHistorial,
  };
  const getComp = (sid) => COMP_MAP[sid];

  const seccionesVisibles = useMemo(
    () => SECCIONES.filter((s) => {
      if (s.soloAlquiler && !esOperacionAlquiler(form.operacion)) return false;
      return puede(s.id, "ver");
    }),
    [form.operacion, puede],
  );

  useEffect(() => {
    if (seccionActiva === "alquiler" && !esOperacionAlquiler(form.operacion)) {
      setSeccionActiva("cabecera");
    }
  }, [form.operacion, seccionActiva]);

  useEffect(() => {
    if (seccionesVisibles.some((s) => s.id === seccionActiva)) return;
    const primera = seccionesVisibles[0]?.id;
    if (primera) setSeccionActiva(primera);
  }, [seccionesVisibles, seccionActiva]);

  // Defaults de alquiler solo en campos vacíos (no pisa valores ya guardados).
  useEffect(() => {
    if (!esOperacionAlquiler(form.operacion)) return;
    setForm((p) => {
      if (!esOperacionAlquiler(p.operacion)) return p;
      const patch = patchAlquilerDefaultsVacios(p);
      return patch ? { ...p, ...patch } : p;
    });
  }, [form.operacion]);

  // Glow igual que Dashboard
  const navGlow = (color) => isDark
    ? `0 0 30px 8px ${color}25, 0 0 60px 20px ${color}15`
    : `0 0 20px 6px ${color}20, 0 0 40px 15px ${color}10`;

  const idiomaCtx = useMemo(
    () => ({ idiomaVista, setIdiomaVista, labelsMap, soloLecturaIdioma, t: tLabel, tv: tvLabel }),
    [idiomaVista, labelsMap, soloLecturaIdioma, tLabel, tvLabel],
  );

  const tituloModalFaltantes = modalFaltantesModo === "alta_directa"
    ? (modalCamposFaltantes.some((x) => /referencia/i.test(String(x)))
      ? "La referencia ya existe"
      : "Faltan campos obligatorios")
    : "Faltan datos mínimos para la ficha de propiedad";

  const modalCamposFaltantesNode = mostrarModalRevisar ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: 16,
          padding: "32px 36px",
          maxWidth: 480,
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: c.textPrimary, margin: "0 0 12px 0" }}>
          {tituloModalFaltantes}
        </h2>
        <p style={{ fontSize: 14, color: c.textSecondary, margin: "0 0 16px 0" }}>
          {modalFaltantesModo === "alta_directa" ? (
            <>
              Para dar de alta en <strong>Captaciones</strong> (y que Marco inicie el proceso)
              debes resolver esto:
            </>
          ) : (
            <>
              Este formulario es el <strong>alta de propiedad</strong> en el inventario (no el pipeline de captación). Faltan estos campos para un alta completa:
            </>
          )}
        </p>
        <ul style={{ margin: "0 0 24px 0", paddingLeft: 20 }}>
          {modalCamposFaltantes.map((campo) => (
            <li key={String(campo)} style={{ fontSize: 14, color: c.accentRed, marginBottom: 4, fontWeight: 600 }}>
              {campo}
            </li>
          ))}
        </ul>
        {modalFaltantesModo === "alta_directa" ? (
          <p style={{ fontSize: 13, color: c.textMuted, margin: "0 0 24px 0" }}>
            El borrador ya está guardado. Si la referencia está ocupada, cámbiala en cabecera
            (o deja que se asigne automáticamente) y vuelve a pulsar «Finalizar → Captaciones».
            No irá a Visita concertada ni a Para Revisar.
          </p>
        ) : (
          <p style={{ fontSize: 13, color: c.textMuted, margin: "0 0 24px 0" }}>
            Puedes volver al formulario o crear la ficha igualmente; luego complétala desde el listado de propiedades. Esto no mueve ninguna captación a «Para Revisar».
          </p>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => setMostrarModalRevisar(false)}
            style={{
              padding: "10px 20px",
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              color: c.textSecondary,
              background: c.bgCard,
              cursor: "pointer",
            }}
          >
            Volver y completar
          </button>
          {modalFaltantesModo !== "alta_directa" && (
            <button
              type="button"
              onClick={confirmarGuardarRevisar}
              style={{
                padding: "10px 24px",
                background: "linear-gradient(135deg, #d97706, #b45309)",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(217,119,6,0.35)",
              }}
            >
              Crear ficha incompleta
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const modalCamposFaltantesPortal =
    modalCamposFaltantesNode && typeof document !== "undefined"
      ? createPortal(modalCamposFaltantesNode, document.body)
      : null;

  if (cargando) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: c.textMuted, fontSize: 14 }}>{tvLabel("Cargando propiedad...")}</p>
        {modalCamposFaltantesPortal}
      </div>
    );
  }

  return (
    <PropiedadIdiomaProvider value={idiomaCtx}>
    <div style={{ background: c.bgApp, minHeight: "100vh" }}>

      {/* ── NAV DE SECCIONES — estilo botones Dashboard ────────────────────── */}
      <nav style={{
        background: c.bgPanel,
        borderBottom: `1px solid ${c.sectionBorder}`,
        position: "sticky",
        top: 48,
        zIndex: 20,
        padding: "8px 14px",
      }}>
        <div style={{ position: "relative", width: "100%", margin: "0 auto", paddingRight: 46 }}>
          <div style={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 4,
            width: "100%",
          }}>
          {seccionesVisibles.map((s, i) => {
            const activa = seccionActiva === s.id;
            const hover = hoverNav === i;
            const col = s.color;
            const navLabel = tLabel(`seccion:${s.id}`, s.label);
            // En oscuro: fondo transparente/semitransparente, texto blanco siempre
            // En claro: fondo sólido del color cuando activo/hover, texto blanco (col oscuro suficiente)
            const bgColor = isDark
              ? (activa ? col + "40" : hover ? col + "1a" : "transparent")
              : (activa ? col : hover ? col + "dd" : "#ffffff");
            const textColor = isDark
              ? "#ffffff"
              : (activa || hover) ? "#ffffff" : col;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollToSection(s.id)}
                onMouseEnter={() => setHoverNav(i)}
                onMouseLeave={() => setHoverNav(null)}
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  padding: "6px 4px",
                  borderRadius: 8,
                  border: `1px solid ${col}`,
                  background: bgColor,
                  color: textColor,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "all 0.25s ease",
                  boxShadow: (activa || hover) ? navGlow(col) : "none",
                  transform: hover && !activa ? "translateY(-1px)" : "none",
                }}
              >
                {navLabel}
              </button>
            );
          })}
          </div>
          <div style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", zIndex: 21 }}>
            <SelectorIdiomaFicha isDark={isDark} />
          </div>
        </div>
      </nav>

      {/* ── FORMULARIO ─────────────────────────────────────────────────────── */}
      <form
        onSubmit={enviar}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          const tag = e.target?.tagName;
          if (tag === "TEXTAREA") return;
          if (e.target?.closest?.("[data-modal-form]")) return;
          e.preventDefault();
        }}
        style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 8, maxWidth: 1400, margin: "0 auto" }}
      >

        {error && (
          <div style={{
            background: isDark ? "#2d1515" : "#fef2f2",
            border: `1px solid ${c.accentRed}`,
            color: c.accentRed,
            padding: "10px 16px", borderRadius: 10, fontSize: 13, marginBottom: 4,
          }}>
            ⚠️ {textoErrorApi(error, "Error al guardar")}
          </div>
        )}

        {portalSyncFixRef.current && (
          <div style={{
            background: isDark ? "#1e3a5f" : "#eff6ff",
            border: `1px solid ${c.accentBlue || "#3b82f6"}`,
            color: isDark ? "#bfdbfe" : "#1e40af",
            padding: "10px 16px", borderRadius: 10, fontSize: 13, marginBottom: 4,
          }}>
            Corrigiendo fallo de{" "}
            <strong>{portalSyncFixRef.current.portalNombre || "portal"}</strong>
            {portalSyncFixRef.current.error ? `: ${portalSyncFixRef.current.error}` : ""}.
            {" "}Al guardar se encola la publicación (segundo plano) y vuelves a Portales.
          </div>
        )}

        {soloLecturaIdioma && (
          <div style={{
            background: isDark ? "#1e1b4b" : "#eef2ff",
            border: "1px solid #6366f1",
            color: isDark ? "#c7d2fe" : "#4338ca",
            padding: "10px 16px", borderRadius: 10, fontSize: 13, marginBottom: 4,
          }}>
            {tvLabel("Viendo la ficha en otro idioma (solo lectura). Vuelve a Español para editar. Las traducciones se actualizan al guardar cambios en español.")}
          </div>
        )}

        {seccionesVisibles.map((s) => {
          const Comp = getComp(s.id);
          const ep = {};
          if (s.id === "ubicacion") {
            ep.visorAddressContext = visorAddressContext;
          }
          if (s.id === "distribucion") { ep.superficies = superficies; ep.setSuperficies = setSuperficies; }
          if (s.id === "publicacion") {
            ep.superficies = superficies;
            ep.onTextosWebGenerados = persistirTextosWebCaptacionYa;
            ep.soloLecturaIdioma = soloLecturaIdioma;
            ep.idiomaVista = idiomaVista;
          }
          if (s.id === "multimedia") {
            ep.fotos = fotos;
            ep.setFotos = setFotos;
            ep.videosArchivo = videosArchivo;
            ep.setVideosArchivo = setVideosArchivo;
            ep.videosYoutube = videosYoutube;
            ep.setVideosYoutube = setVideosYoutube;
            ep.mostrarMarcoAutoenhance = !!(
              captacionOrigen?.id
              || captacionOrigenRef.current?.id
              || (captacionVinculadaId && captacionValidada)
              || id
              || form.id
            );
            ep.captacionId = captacionOrigen?.id || captacionOrigenRef.current?.id || captacionVinculadaId || null;
            ep.propiedadId = id || form.id || null;
            ep.onFotoEliminadaPersistida = (fotoId) => {
              idsReportajeInicialRef.current?.delete(fotoId);
            };
          }
          if (s.id === "propietarios") {
            ep.propietarios = propietarios;
            ep.setPropietarios = setPropietarios;
            ep.contactosAsociados = contactosAsociados;
            ep.setContactosAsociados = setContactosAsociados;
            ep.contactoComunicacionId = contactoComunicacionId;
            ep.setContactoComunicacionId = setContactoComunicacionId;
            ep.placesLocalityHint = placesLocalityHintFromForm(form);
          }
          if (s.id === "documentacion") {
            ep.documentos = documentos;
            ep.setDocumentos = setDocumentos;
            ep.setDocumentosEliminar = setDocumentosEliminar;
            ep.propiedadId = id || form.id || null;
            ep.defaultsExclusiva = {
              duracion: form.exclusiva_duracion || "",
              desde: form.exclusiva_desde || "",
              hasta: form.exclusiva_hasta || "",
            };
            ep.precioVenta = form.precio_venta || form.precio || "";
          }
          if (s.id === "internos") {
            ep.propiedadId = id || form.id || null;
            ep.historicoRefreshKey = historicoPreciosTick;
            ep.focusPrecioTick = focusPrecioTick;
            ep.focusLlavesTick = focusLlavesTick;
          }
          if (s.id === "distribucion") {
            ep.propiedadId = id || form.id || null;
          }
          if (s.id === "historial") {
            ep.propiedadId = id || form.id || null;
            ep.referencia = form.referencia || "";
          }

          const bloqueadaPorIdioma = soloLecturaIdioma;
          const tituloSeccion = tLabel(`seccion:${s.id}`, s.label);

          return (
            <div
              key={s.id}
              id={"seccion-" + s.id}
              style={{
                background: c.bgCard,
                borderTop: isDark ? `1px solid ${c.sectionBorder}` : `2px solid ${s.color}22`,
                borderRight: `1px solid ${c.sectionBorder}`,
                borderBottom: `1px solid ${c.sectionBorder}`,
                borderLeft: `6px solid ${s.color}`,
                borderRadius: 12,
                padding: "20px 24px",
                scrollMarginTop: 100,
                boxShadow: c.cardShadow,
                pointerEvents: bloqueadaPorIdioma ? "none" : undefined,
                opacity: bloqueadaPorIdioma ? 0.72 : 1,
              }}
            >
              <h2 style={{
                fontSize: 11, fontWeight: 800,
                color: isDark ? "#ffffff" : s.color,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                margin: "0 0 16px 0",
              }}>
                {tituloSeccion}
              </h2>
              <Comp {...pc} {...ep} />
            </div>
          );
        })}

        {/* ── BOTONES GUARDAR ──────────────────────────────────────────────── */}
        <div style={{
          display: "flex", gap: 12, justifyContent: "flex-end", alignItems: "center",
          position: "sticky", bottom: 0,
          background: c.bgApp, padding: "12px 0",
          borderTop: `1px solid ${c.sectionBorder}`, marginTop: 8,
        }}>
          {enFichaCaptacion && (
            <span style={{ marginRight: "auto", fontSize: 12, color: c.textMuted, maxWidth: 420, lineHeight: 1.45 }}>
              {archivosSubiendo ? (
                <>
                  Las fotos y documentos se guardan solos ({subidaFondo.stats.pendientes + subidaFondo.stats.subiendo} en cola).
                  {" "}
                  <strong style={{ color: c.textSecondary }}>
                    {pendingNuevaCaptacion
                      ? "Puedes finalizar cuando terminen: irá directo a Captaciones (no a Visita ni Para Revisar)."
                      : "Puedes finalizar ya: pasará a Para Revisar cuando terminen de subirse."}
                  </strong>
                </>
              ) : archivosConError ? (
                <>Hay archivos con error — reintenta abajo. Puedes finalizar igual; no pasará a Para Revisar hasta que se suban todos.</>
              ) : autosaveActivo ? (
                "Guardando borrador…"
              ) : borradorGuardadoAt ? (
                <>
                  Borrador guardado {new Date(borradorGuardadoAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}.
                  {" "}
                  {fotos.length + documentos.length + videosArchivo.length > 0
                    ? "Todo subido — ya puedes finalizar."
                    : null}
                </>
              ) : (
                "Los cambios se guardan solos a los pocos segundos. También al cerrar la ficha o con «Guardar borrador»."
              )}
            </span>
          )}
          <button type="button" onClick={() => { cancelarFormulario(); }}
            style={{
              padding: "10px 24px", border: `1px solid ${c.border}`,
              borderRadius: 10, fontSize: 13, fontWeight: 600,
              color: c.textSecondary, background: c.bgCard, cursor: "pointer",
            }}>
            {tvLabel("Cancelar")}
          </button>
          {enFichaCaptacion && (
            <button
              type="button"
              disabled={enviando || cargando}
              onClick={() => guardarBorradorCaptacion()}
              style={{
                padding: "10px 24px",
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                color: c.textPrimary,
                background: c.bgCard,
                cursor: enviando || cargando ? "not-allowed" : "pointer",
              }}
            >
              {enviando ? tvLabel("Guardando…") : tvLabel("Guardar borrador")}
            </button>
          )}
          {enFichaCaptacion ? (
            <button
              type="button"
              disabled={finalizando || cargando}
              title={
                archivosSubiendo
                  ? (pendingNuevaCaptacion
                    ? "Espera a que terminen las subidas para dar de alta en Captaciones"
                    : "Finalizar ahora; las fotos seguirán subiendo y pasará a Para Revisar cuando terminen")
                  : undefined
              }
              onClick={async () => {
                const capResuelta = await resolverCaptacionAlGuardar();
                if (capResuelta?.id && !fromStateCaptacion?.id) setCaptacionFromRecovery(capResuelta);
                await guardarFichaEnCaptacion(capResuelta);
              }}
              style={{
                padding: "10px 32px",
                background: finalizando ? c.border : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                color: "#fff", border: "none", borderRadius: 10,
                fontSize: 13, fontWeight: 700,
                cursor: finalizando || cargando ? "not-allowed" : "pointer",
                boxShadow: finalizando ? "none" : "0 2px 8px rgba(37,99,235,0.35)",
              }}
            >
              {finalizando
                ? "Finalizando…"
                : archivosSubiendo
                  ? `✓ Finalizar (${subidaFondo.stats.pendientes + subidaFondo.stats.subiendo} subiendo)`
                  : tvLabel(pendingNuevaCaptacion ? "✓ Finalizar → Captaciones" : "✓ Finalizar ficha")}
            </button>
          ) : (
            <button type="submit" disabled={enviando || cargando}
              style={{
                padding: "10px 32px",
                background: enviando ? c.border : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                color: "#fff", border: "none", borderRadius: 10,
                fontSize: 13, fontWeight: 700,
                cursor: enviando || cargando ? "not-allowed" : "pointer",
                boxShadow: enviando ? "none" : "0 2px 8px rgba(37,99,235,0.35)",
              }}>
              {enviando ? "Guardando..." : (esEdicion ? "✓ Actualizar propiedad" : "✓ Guardar propiedad")}
            </button>
          )}
        </div>
      </form>

      {enFichaCaptacion && (
        <BarraSubidaFondo
          stats={subidaFondo.stats}
          onReintentar={subidaFondo.reintentarErrores}
        />
      )}

      {/* Modal campos faltantes → portal a body (z-index alto; también si cargando) */}
      {modalCamposFaltantesPortal}

      {bajadaFlow && !modalEnvioBajada && (
        <ModalConfirmLeadsBajadaPrecio
          isOpen
          leads={bajadaFlow.leads}
          bajada={bajadaFlow.bajada}
          referencia={bajadaFlow.referencia}
          onConfirm={(seleccionados) => {
            setLeadsBajadaEnvio(seleccionados);
            setModalEnvioBajada(true);
          }}
          onCancel={finalizarTrasBajadaPrecio}
        />
      )}
      {bajadaFlow && modalEnvioBajada && (
        <ModalEnvioMasivoFrios
          isOpen
          leads={leadsBajadaEnvio}
          propiedadInicial={{ referencia: bajadaFlow.referencia }}
          mensajeInicial={mensajeDefaultBajada(bajadaFlow.bajada.importe)}
          bloquearQuitarPropiedad
          modoBajadaAna
          propiedadId={bajadaFlow.propiedadId || id || form.id}
          onClose={finalizarTrasBajadaPrecio}
          onComplete={finalizarTrasBajadaPrecio}
        />
      )}

      <ModalRepublicarPortales
        isOpen={!!modalPortalesPrevios?.length}
        items={modalPortalesPrevios || []}
        onClose={() => {
          setModalPortalesPrevios(null);
          navigateFormExit();
        }}
        onDone={() => {
          setModalPortalesPrevios(null);
          navigateFormExit();
        }}
      />

    </div>
    </PropiedadIdiomaProvider>
  );
}
